import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { shipService, Ship, ShipHardpoint } from '../services/shipService';
import { shipReviewService, ShipReview, ReviewSummary, SortOption } from '../services/shipReviewService';
import { useAuth } from '../context/AuthContext';
import { PowerManagementPanel, PowerAllocation } from '../components/power';
import { StealthProfilePanel } from '../components/power/StealthProfilePanel';
import { EmbeddedWeaponList } from '../components/weapons';
import {
  DoorOpen, 
  Crosshair, Zap, Shield, Battery, Sparkles,
  Target, Rocket, Gauge, Clock, RefreshCw, Thermometer,
  ChevronDown, Navigation, Fuel, Box, Flame, Wind,
  ArrowLeft, Swords, ShieldCheck, Anchor, Disc, Pickaxe,
  Users, Move, Cpu, Settings, Wrench, Lock, Heart, Radio,
  AlertTriangle, CheckCircle, HelpCircle, X, Star, ThumbsUp, ThumbsDown,
  MessageSquare, Edit2, Trash2, Info
} from 'lucide-react';
import styles from './ShipDetailV2.module.css';

// Info Tooltip Component
const InfoTooltip: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className={styles.infoTooltipWrapper}>
      <button 
        className={styles.infoButton}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={`Info about ${title}`}
      >
        <HelpCircle size={14} />
      </button>
      {isOpen && (
        <>
          <div className={styles.infoBackdrop} onClick={() => setIsOpen(false)} />
          <div className={styles.infoTooltip}>
            <div className={styles.infoTooltipHeader}>
              <span>{title}</span>
              <button onClick={() => setIsOpen(false)} className={styles.infoCloseBtn}>
                <X size={14} />
              </button>
            </div>
            <div className={styles.infoTooltipContent}>
              {children}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Format large numbers compactly
const formatCompact = (num: number): string => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toFixed(0);
};

// Helper to check if a string is a GUID (should not be displayed as manufacturer)
const isGuid = (value: string | null | undefined): boolean => {
  if (!value) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
};

// Known manufacturer GUIDs from P4K data
const knownManufacturerGuids: Record<string, string> = {
  '1e47a9ec-a3f0-431d-be0e-377712e1ed84': 'Origin Jumpworks',
};

// Get valid manufacturer (resolves GUIDs to names, returns empty string if unresolvable GUID)
const getValidManufacturer = (manufacturer: string | null | undefined): string => {
  if (!manufacturer) return '';
  const resolved = knownManufacturerGuids[manufacturer.toLowerCase()];
  if (resolved) return resolved;
  if (isGuid(manufacturer)) return '';
  return manufacturer;
};

// Tab definitions
type TabId = 'overview' | 'combat' | 'engineering' | 'loadout' | 'facilities' | 'reviews';

const ShipDetailV2: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const isAuthenticated = !!token && !!user;
  const [ship, setShip] = useState<Ship | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [facilities, setFacilities] = useState<import('../services/shipService').ShipFacility[] | null>(null);
  const [facilitiesFailed, setFacilitiesFailed] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['weapons', 'shields']));
  
  // Power allocations for emissions panel reactivity
  const [powerAllocations, setPowerAllocations] = useState<PowerAllocation[]>([]);
  
  // Reviews state
  const [reviews, setReviews] = useState<ShipReview[]>([]);
  const [reviewSummary, setReviewSummary] = useState<ReviewSummary | null>(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [reviewsTotalPages, setReviewsTotalPages] = useState(1);
  const [reviewsSort, setReviewsSort] = useState<SortOption>('helpful');
  const [userReviewId, setUserReviewId] = useState<number | null>(null);
  const [myVotes, setMyVotes] = useState<Record<number, boolean>>({});
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [editingReview, setEditingReview] = useState<ShipReview | null>(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  useEffect(() => {
    if (slug) {
      fetchShip(slug);
    }
  }, [slug]);

  // Fetch reviews when switching to reviews tab or ship changes
  useEffect(() => {
    if (ship?.slug && activeTab === 'facilities' && facilities === null && !facilitiesFailed) {
      shipService
        .getFacilities(ship.slug)
        .then(setFacilities)
        .catch(() => setFacilitiesFailed(true));
    }
    if (ship?.id && activeTab === 'reviews') {
      fetchReviews();
    }
  }, [ship?.id, activeTab, reviewsPage, reviewsSort]);

  // Fetch user's votes when authenticated
  useEffect(() => {
    if (ship?.id && activeTab === 'reviews' && isAuthenticated) {
      fetchMyVotes();
    }
  }, [ship?.id, activeTab, isAuthenticated]);

  const fetchShip = async (shipSlug: string) => {
    try {
      setLoading(true);
      const data = await shipService.getShipBySlug(shipSlug);
      setShip(data);
      setError(null);
    } catch (err) {
      setError('Failed to load ship data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    if (!ship?.id) return;
    try {
      setReviewsLoading(true);
      const response = await shipReviewService.getReviews(ship.id, reviewsPage, 20, reviewsSort);
      setReviews(response.reviews);
      setReviewSummary(response.summary);
      setReviewsTotalPages(response.pagination.totalPages);
      setUserReviewId(response.currentUserReviewId || null);
    } catch (err) {
      console.error('Error fetching reviews:', err);
    } finally {
      setReviewsLoading(false);
    }
  };

  const fetchMyVotes = async () => {
    if (!ship?.id) return;
    try {
      const votes = await shipReviewService.getMyVotes(ship.id);
      setMyVotes(votes);
    } catch (err) {
      // Not authenticated or error - that's fine
      console.error('Error fetching votes:', err);
    }
  };

  const handleSubmitReview = async () => {
    if (!ship?.id) return;
    try {
      setReviewSubmitting(true);
      setReviewError(null);
      
      if (editingReview) {
        await shipReviewService.updateReview(ship.id, editingReview.id, {
          rating: reviewRating,
          reviewText: reviewText || undefined
        });
      } else {
        await shipReviewService.createReview(ship.id, {
          rating: reviewRating,
          reviewText: reviewText || undefined
        });
      }
      
      // Reset form and refresh
      setShowReviewForm(false);
      setEditingReview(null);
      setReviewRating(5);
      setReviewText('');
      await fetchReviews();
    } catch (err: any) {
      setReviewError(err.response?.data?.message || 'Failed to submit review');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handleDeleteReview = async (reviewId: number) => {
    if (!ship?.id || !window.confirm('Are you sure you want to delete your review?')) return;
    try {
      await shipReviewService.deleteReview(ship.id, reviewId);
      await fetchReviews();
    } catch (err) {
      console.error('Error deleting review:', err);
    }
  };

  const handleVote = async (reviewId: number, isUpvote: boolean) => {
    if (!ship?.id || !isAuthenticated) return;
    try {
      const response = await shipReviewService.voteOnReview(ship.id, reviewId, isUpvote);
      // Update local state
      setReviews(prev => prev.map(r => 
        r.id === reviewId 
          ? { ...r, upvoteCount: response.upvoteCount, downvoteCount: response.downvoteCount, helpfulnessScore: response.helpfulnessScore }
          : r
      ));
      // Toggle vote in local state
      setMyVotes(prev => {
        const current = prev[reviewId];
        if (current === isUpvote) {
          // Same vote - remove it
          const { [reviewId]: _, ...rest } = prev;
          return rest;
        }
        // Different or new vote
        return { ...prev, [reviewId]: isUpvote };
      });
    } catch (err) {
      console.error('Error voting:', err);
    }
  };

  const startEditReview = (review: ShipReview) => {
    setEditingReview(review);
    setReviewRating(review.rating);
    setReviewText(review.reviewText || '');
    setShowReviewForm(true);
    setReviewError(null);
  };

  const cancelReviewForm = () => {
    setShowReviewForm(false);
    setEditingReview(null);
    setReviewRating(5);
    setReviewText('');
    setReviewError(null);
  };

  // Calculate stats from hardpoints
  const stats = useMemo(() => {
    if (!ship?.hardpoints) return null;

    const shields = ship.hardpoints.filter(hp => hp.hardpointType === 'Shield');
    const powerPlants = ship.hardpoints.filter(hp => hp.hardpointType === 'PowerPlant');
    const coolers = ship.hardpoints.filter(hp => hp.hardpointType === 'Cooler');
    const thrusters = ship.hardpoints.filter(hp => 
      hp.hardpointType === 'Thruster' || hp.hardpointType === 'MainThruster'
    );
    const turrets = ship.hardpoints.filter(hp => hp.hardpointType === 'Turret');
    // Filter out controller_weapon hardpoints and nacelles - they're not actual weapons
    const standaloneWeapons = ship.hardpoints.filter(hp => {
      if (hp.hardpointType !== 'Weapon') return false;
      const slotName = (hp.slotName || '').toLowerCase();
      const defaultName = (hp.defaultItemName || '').toLowerCase();
      const localizedName = (hp.itemDefinition?.localizedName || '').toLowerCase();
      // Exclude controller hardpoints
      if (slotName.includes('controller_weapon') || slotName.includes('controller_')) return false;
      if (defaultName.includes('controller_weapon') || defaultName.includes('controller_')) return false;
      // Exclude nacelles (engine/flight components incorrectly categorized as weapons)
      if (defaultName.includes('nacelle') || localizedName.includes('nacelle')) return false;
      return true;
    });
    const quantumDrives = ship.hardpoints.filter(hp => 
      hp.hardpointType === 'QuantumDrive' && 
      hp.itemDefinition?.type !== 'QuantumFuelTank'
    );
    // EMPs and quantum snares/dampeners — typed from the item they hold.
    const interdiction = ship.hardpoints.filter(hp =>
      hp.hardpointType === 'EMP' || hp.hardpointType === 'QuantumInterdiction'
    );

    const totalShieldHP = shields.reduce((sum, hp) => sum + (hp.itemDefinition?.shieldStats?.shieldHealthMax || 0), 0);
    const totalShieldRegen = shields.reduce((sum, hp) => sum + (hp.itemDefinition?.shieldStats?.regenRate || 0), 0);
    const totalPowerOutput = powerPlants.reduce((sum, hp) => sum + (hp.itemDefinition?.powerPlantStats?.powerOutput || 0), 0);
    const totalCooling = coolers.reduce((sum, hp) => sum + (hp.itemDefinition?.coolerStats?.coolingRate || 0), 0);
    
    // Helper to calculate DPS from a weapon item
    const calcWeaponDPS = (itemDef: any): number => {
      if (!itemDef?.weaponStats) return 0;
      const dmg = itemDef.weaponStats.damagePerShot || 0;
      const rpm = itemDef.weaponStats.fireRate || 60;
      return dmg * rpm / 60;
    };

    // Helper to determine damage type from weapon stats or item name
    const getDamageType = (itemDef: any, itemName?: string): 'ballistic' | 'energy' | 'distortion' | 'unknown' => {
      // Check explicit damage type
      const damageType = (itemDef?.weaponStats?.damageType || '').toLowerCase();
      if (damageType.includes('ballistic') || damageType.includes('physical')) return 'ballistic';
      if (damageType.includes('energy') || damageType.includes('laser')) return 'energy';
      if (damageType.includes('distortion') || damageType.includes('emp')) return 'distortion';
      
      // Infer from item name
      const name = (itemName || itemDef?.localizedName || '').toLowerCase();
      if (name.includes('repeater') || name.includes('cannon') || name.includes('gatling') || 
          name.includes('scattergun') || name.includes('ballistic')) return 'ballistic';
      if (name.includes('laser') || name.includes('plasma') || name.includes('beam') || 
          name.includes('neutron') || name.includes('pulsar')) return 'energy';
      if (name.includes('distortion') || name.includes('suckerpunch') || name.includes('emp')) return 'distortion';
      
      return 'unknown';
    };

    // Enhanced weapon data type
    type WeaponData = { 
      size: number; 
      dps: number; 
      name: string; 
      damageType: 'ballistic' | 'energy' | 'distortion' | 'unknown';
      range: number;
      alpha: number; // damage per shot
    };

    // Helper to recursively get all weapons from loadedItems (handles gimbals with childAttachments)
    const getWeaponsFromLoadedItems = (loadedItems: any[]): WeaponData[] => {
      const weapons: WeaponData[] = [];
      
      // Build a Set of all weapon IDs in childAttachments to prevent duplicates
      // The backend flattens hierarchy: weapons appear BOTH as direct loadedItems AND in childAttachments
      const childWeaponIds = new Set<number>();
      loadedItems.forEach(item => {
        if (item.childAttachments && item.childAttachments.length > 0) {
          item.childAttachments.forEach((child: any) => {
            if (child.id) childWeaponIds.add(child.id);
          });
        }
      });
      
      loadedItems.forEach(item => {
        const itemName = (item.itemName || '').toLowerCase();
        const itemType = (item.itemDefinition?.type || '').toLowerCase();
        const localizedName = (item.itemDefinition?.localizedName || '').toLowerCase();
        const displayNameLower = (item.displayName || '').toLowerCase();
        
        // SKIP internal controller/system items - these are NOT actual weapons
        // controller_weapon is an internal game object that controls weapon firing, not the gun itself
        const isInternalItem = itemName.includes('controller_weapon') || 
                               itemName.includes('controller_') ||
                               itemName.includes('turretbase') ||
                               localizedName.includes('controller_weapon') ||
                               displayNameLower.includes('controller_weapon') ||
                               itemType === 'turretbase' ||
                               itemType === 'turret'; // "Turret" type in loadedItems is a mount, not a weapon
        
        // Check if this item is an actual weapon (has weaponStats and is type Weapon/WeaponGun)
        // MUST be explicitly type 'weapon' or 'weapongun' - don't allow empty type with weaponStats
        const isActualWeapon = item.itemDefinition?.weaponStats && 
                               (itemType === 'weapon' || itemType === 'weapongun');
        
        if (isActualWeapon && !isInternalItem) {
          // Skip if this weapon is a duplicate (appears in childAttachments)
          if (item.id && childWeaponIds.has(item.id)) {
            return; // Skip - will be processed from childAttachments
          }
          
          const itemDef = item.itemDefinition;
          const displayName = itemDef.localizedName || item.displayName || item.itemName;
          
          // Skip items with placeholder or controller names
          if (displayName && !displayName.toLowerCase().includes('controller') && 
              !displayName.toLowerCase().includes('placeholder')) {
            weapons.push({
              size: itemDef.size || item.size || 0,
              dps: calcWeaponDPS(itemDef),
              name: displayName,
              damageType: getDamageType(itemDef, item.itemName),
              range: itemDef.weaponStats?.range || 0,
              alpha: itemDef.weaponStats?.damagePerShot || 0
            });
          }
        }
        // Check childAttachments (weapons mounted on gimbals)
        if (item.childAttachments && item.childAttachments.length > 0) {
          weapons.push(...getWeaponsFromLoadedItems(item.childAttachments));
        }
      });
      return weapons;
    };

    // Calculate total DPS from ALL weapons (standalone + turret-mounted)
    let totalDPS = 0;
    let totalAlpha = 0; // Alpha strike damage
    let ballisticDPS = 0;
    let energyDPS = 0;
    let distortionDPS = 0;
    let maxWeaponRange = 0;
    let minWeaponRange = Infinity;
    const allWeapons: WeaponData[] = [];
    const weaponsBySize: Record<number, { count: number; dps: number; names: string[] }> = {};

    // Standalone weapons - check loadedItems for actual weapons (handles mounts/gimbals)
    standaloneWeapons.forEach(hp => {
      // Check if the weapon has loadedItems (it's a mount with weapons loaded)
      if (hp.loadedItems && hp.loadedItems.length > 0) {
        // Special case: Check if loadedItems contains weapon PARTS (Barrel, FiringMechanism, etc.)
        // This happens with Ares-style weapons where the hardpoint IS the weapon
        // and loadedItems are internal parts, not the actual gun
        const firstItem = hp.loadedItems[0];
        const firstItemType = (firstItem.itemDefinition?.type || '').toLowerCase();
        const firstItemSubType = (firstItem.itemDefinition?.subType || '').toLowerCase();
        const isWeaponPart = firstItemType === 'weaponattachment' || 
                            ['barrel', 'firingmechanism', 'powerarray', 'ventilation'].includes(firstItemSubType);
        
        if (isWeaponPart && hp.itemDefinition?.weaponStats) {
          // Use the hardpoint's itemDefinition (it IS the weapon)
          const itemDef = hp.itemDefinition;
          const dps = calcWeaponDPS(itemDef);
          const size = hp.size || itemDef.size || 0;
          const name = itemDef.localizedName || hp.defaultItemDisplayName || '';
          const damageType = getDamageType(itemDef, hp.defaultItemName || undefined);
          const range = itemDef.weaponStats?.range || 0;
          const alpha = itemDef.weaponStats?.damagePerShot || 0;
          
          if (dps > 0 && !name.toLowerCase().includes('placeholder')) {
            totalDPS += dps;
            totalAlpha += alpha;
            allWeapons.push({ size, dps, name, damageType, range, alpha });
            
            if (damageType === 'ballistic') ballisticDPS += dps;
            else if (damageType === 'energy') energyDPS += dps;
            else if (damageType === 'distortion') distortionDPS += dps;
            
            if (range > 0) {
              maxWeaponRange = Math.max(maxWeaponRange, range);
              minWeaponRange = Math.min(minWeaponRange, range);
            }
            
            if (!weaponsBySize[size]) weaponsBySize[size] = { count: 0, dps: 0, names: [] };
            weaponsBySize[size].count++;
            weaponsBySize[size].dps += dps;
            if (name && !weaponsBySize[size].names.includes(name)) {
              weaponsBySize[size].names.push(name);
            }
          }
        } else {
          // Normal case: loadedItems contains actual weapons (mounts/gimbals)
          const weaponsFromHardpoint = getWeaponsFromLoadedItems(hp.loadedItems);
          weaponsFromHardpoint.forEach(weapon => {
            totalDPS += weapon.dps;
            totalAlpha += weapon.alpha;
            allWeapons.push(weapon);
            
            // Track DPS by damage type
            if (weapon.damageType === 'ballistic') ballisticDPS += weapon.dps;
            else if (weapon.damageType === 'energy') energyDPS += weapon.dps;
            else if (weapon.damageType === 'distortion') distortionDPS += weapon.dps;
            
            // Track weapon range
            if (weapon.range > 0) {
              maxWeaponRange = Math.max(maxWeaponRange, weapon.range);
              minWeaponRange = Math.min(minWeaponRange, weapon.range);
            }
            
            if (!weaponsBySize[weapon.size]) weaponsBySize[weapon.size] = { count: 0, dps: 0, names: [] };
            weaponsBySize[weapon.size].count++;
            weaponsBySize[weapon.size].dps += weapon.dps;
            if (weapon.name && !weaponsBySize[weapon.size].names.includes(weapon.name)) {
              weaponsBySize[weapon.size].names.push(weapon.name);
            }
          });
        }
      } else {
        // Direct weapon (no mount/gimbal)
        // Skip if this is a controller or internal item (additional safety check)
        const hpName = (hp.defaultItemName || '').toLowerCase();
        const hpSlot = (hp.slotName || '').toLowerCase();
        const itemType = (hp.itemDefinition?.type || '').toLowerCase();
        if (hpName.includes('controller') || hpSlot.includes('controller') || 
            itemType === 'turret' || itemType === 'turretbase') {
          return; // Skip this hardpoint
        }
        
        const dps = calcWeaponDPS(hp.itemDefinition);
        // Skip weapons with no DPS (they're not real weapons)
        if (dps <= 0) return;
        
        const size = hp.size || hp.itemDefinition?.size || 0;
        const name = hp.itemDefinition?.localizedName || hp.defaultItemDisplayName || '';
        
        // Skip items with bad names
        if (name.toLowerCase().includes('controller') || name.toLowerCase().includes('placeholder')) {
          return;
        }
        
        const damageType = getDamageType(hp.itemDefinition, hp.defaultItemName || undefined);
        const range = hp.itemDefinition?.weaponStats?.range || 0;
        const alpha = hp.itemDefinition?.weaponStats?.damagePerShot || 0;
        
        totalDPS += dps;
        totalAlpha += alpha;
        allWeapons.push({ size, dps, name, damageType, range, alpha });
        
        // Track DPS by damage type
        if (damageType === 'ballistic') ballisticDPS += dps;
        else if (damageType === 'energy') energyDPS += dps;
        else if (damageType === 'distortion') distortionDPS += dps;
        
        // Track weapon range
        if (range > 0) {
          maxWeaponRange = Math.max(maxWeaponRange, range);
          minWeaponRange = Math.min(minWeaponRange, range);
        }
        
        if (!weaponsBySize[size]) weaponsBySize[size] = { count: 0, dps: 0, names: [] };
        weaponsBySize[size].count++;
        weaponsBySize[size].dps += dps;
        if (name && !weaponsBySize[size].names.includes(name)) {
          weaponsBySize[size].names.push(name);
        }
      }
    });

    // Turret-mounted weapons (from loadedItems and their childAttachments)
    turrets.forEach(turret => {
      if (turret.loadedItems) {
        const turretWeapons = getWeaponsFromLoadedItems(turret.loadedItems);
        turretWeapons.forEach(weapon => {
          totalDPS += weapon.dps;
          totalAlpha += weapon.alpha;
          allWeapons.push(weapon);
          
          // Track DPS by damage type
          if (weapon.damageType === 'ballistic') ballisticDPS += weapon.dps;
          else if (weapon.damageType === 'energy') energyDPS += weapon.dps;
          else if (weapon.damageType === 'distortion') distortionDPS += weapon.dps;
          
          // Track weapon range
          if (weapon.range > 0) {
            maxWeaponRange = Math.max(maxWeaponRange, weapon.range);
            minWeaponRange = Math.min(minWeaponRange, weapon.range);
          }
          
          if (!weaponsBySize[weapon.size]) weaponsBySize[weapon.size] = { count: 0, dps: 0, names: [] };
          weaponsBySize[weapon.size].count++;
          weaponsBySize[weapon.size].dps += weapon.dps;
          if (weapon.name && !weaponsBySize[weapon.size].names.includes(weapon.name)) {
            weaponsBySize[weapon.size].names.push(weapon.name);
          }
        });
      }
    });

    // Get ALL power-drawing systems for comprehensive engineering display
    const radars = ship.hardpoints.filter(hp => hp.hardpointType === 'Radar' || hp.itemDefinition?.type === 'Radar');
    const avionics = ship.hardpoints.filter(hp => 
      hp.hardpointType === 'Avionics' || hp.itemDefinition?.type === 'FlightController'
    );
    const lifeSupport = ship.hardpoints.filter(hp => 
      hp.itemDefinition?.type === 'LifeSupportGenerator' || 
      (hp.defaultItemName || '').toLowerCase().includes('life_support')
    );
    const gravityGenerators = ship.hardpoints.filter(hp => 
      hp.itemDefinition?.type === 'GravityGenerator' ||
      (hp.defaultItemName || '').toLowerCase().includes('gravity_generator')
    );
    const weapons = ship.hardpoints.filter(hp => hp.hardpointType === 'Weapon');

    // Power consumption calculations - using PowerBase (idle consumption)
    // PowerBase = power consumed when system is on but idle
    // PowerDraw = maximum power consumed at full load (combat)
    const shieldPowerDraw = shields.reduce((sum, hp) => sum + (hp.itemDefinition?.powerBase || 0), 0);
    const coolerPowerDraw = coolers.reduce((sum, hp) => sum + (hp.itemDefinition?.powerBase || 0), 0);
    const thrusterPowerDraw = thrusters.reduce((sum, hp) => sum + (hp.itemDefinition?.powerBase || 0), 0);
    const qdPowerDraw = quantumDrives.reduce((sum, hp) => sum + (hp.itemDefinition?.powerBase || 0), 0);
    const radarPowerDraw = radars.reduce((sum, hp) => sum + (hp.itemDefinition?.powerBase || 0), 0);
    const avionicsPowerDraw = avionics.reduce((sum, hp) => sum + (hp.itemDefinition?.powerBase || 0), 0);
    const lifeSupportPowerDraw = lifeSupport.reduce((sum, hp) => sum + (hp.itemDefinition?.powerBase || 0), 0);
    const gravityPowerDraw = gravityGenerators.reduce((sum, hp) => sum + (hp.itemDefinition?.powerBase || 0), 0);
    const weaponPowerDraw = weapons.reduce((sum, hp) => sum + (hp.itemDefinition?.powerBase || 0), 0);
    
    // Turret power consumption (from turret itself + loaded weapons) - using PowerBase
    const turretPowerDraw = turrets.reduce((sum, turret) => {
      let turretTotal = turret.itemDefinition?.powerBase || 0;
      if (turret.loadedItems) {
        turret.loadedItems.forEach(item => {
          turretTotal += item.itemDefinition?.powerBase || 0;
          if (item.childAttachments) {
            item.childAttachments.forEach((child: any) => {
              turretTotal += child.itemDefinition?.powerBase || 0;
            });
          }
        });
      }
      return sum + turretTotal;
    }, 0);
    
    // Total idle power consumption = ALL systems using PowerBase
    // This represents power draw when ship is on but systems are idle
    const basePowerDraw = shieldPowerDraw + coolerPowerDraw + thrusterPowerDraw + 
                          radarPowerDraw + avionicsPowerDraw + lifeSupportPowerDraw + 
                          gravityPowerDraw + weaponPowerDraw + turretPowerDraw + qdPowerDraw;
    const powerUtilization = totalPowerOutput > 0 ? Math.round((basePowerDraw / totalPowerOutput) * 100) : 0;
    const powerSurplus = totalPowerOutput - basePowerDraw;

    // Missile/torpedo count and breakdown by size
    let totalMissileDamage = 0;
    let missileCount = 0;
    const missilesBySize: Record<number, { count: number; damage: number; type: string }> = {};
    
    // Bomb count and damage tracking (separate from missiles)
    let totalBombDamage = 0;
    let bombCount = 0;
    const bombsBySize: Record<number, { count: number; damage: number }> = {};
    
    // Helper to process an item with missileStats (could be missile or bomb)
    const processMissileOrBomb = (item: any) => {
      const itemName = (item.itemName || item.itemDefinition?.itemName || '').toLowerCase();
      const itemType = (item.itemDefinition?.type || '').toLowerCase();
      const size = item.itemDefinition?.size || 0;
      
      // Check if this is a bomb (by type or name pattern)
      const isBombItem = itemType === 'bomb' || itemName.startsWith('bomb_');
      
      if (isBombItem) {
        // Track as bomb - use missileStats.damage if available, else estimate from size
        const damage = item.itemDefinition?.missileStats?.damage || 0;
        bombCount++;
        totalBombDamage += damage;
        
        if (!bombsBySize[size]) {
          bombsBySize[size] = { count: 0, damage: 0 };
        }
        bombsBySize[size].count++;
        bombsBySize[size].damage += damage;
      } else if (item.itemDefinition?.missileStats?.damage) {
        // Track as missile/torpedo (only if has damage data)
        const damage = item.itemDefinition.missileStats.damage;
        missileCount++;
        totalMissileDamage += damage;
        
        // Determine type (torpedo = size 9+, missile = smaller)
        const type = size >= 9 ? 'Torpedo' : 'Missile';
        
        if (!missilesBySize[size]) {
          missilesBySize[size] = { count: 0, damage: 0, type };
        }
        missilesBySize[size].count++;
        missilesBySize[size].damage += damage;
      }
    };
    
    ship.hardpoints.forEach(hp => {
      if (hp.loadedItems) {
        hp.loadedItems.forEach(item => {
          // Check item directly
          processMissileOrBomb(item);
          
          // Also check childAttachments (for bombs in bomb racks, missiles in racks)
          if (item.childAttachments) {
            item.childAttachments.forEach((child: any) => {
              processMissileOrBomb(child);
            });
          }
        });
      }
    });

    // Armor stats
    const armorHardpoints = ship.hardpoints.filter(hp => hp.hardpointType === 'Armor');
    const totalArmorHP = ship.armorHealth || armorHardpoints.reduce((sum, hp) => sum + (hp.itemDefinition?.health || 0), 0);
    const armorType = ship.armorType || (armorHardpoints.length > 0 ? armorHardpoints[0].itemDefinition?.subType : null);
    
    // Armor damage reduction calculation
    const armorDamageReduction = (() => {
      if (ship.armorPhysicalDamageMultiplier && ship.armorEnergyDamageMultiplier) {
        const physical = Math.round((1 - ship.armorPhysicalDamageMultiplier) * 100);
        const energy = Math.round((1 - ship.armorEnergyDamageMultiplier) * 100);
        const distortion = ship.armorDistortionDamageMultiplier 
          ? Math.round((1 - ship.armorDistortionDamageMultiplier) * 100) : 0;
        return { physical, energy, distortion };
      }
      
      const armorDef = armorHardpoints.length > 0 ? armorHardpoints[0].itemDefinition : null;
      if (armorDef?.armorStats?.damagePhysical && armorDef?.armorStats?.damageEnergy) {
        const physical = Math.round((1 - armorDef.armorStats.damagePhysical) * 100);
        const energy = Math.round((1 - armorDef.armorStats.damageEnergy) * 100);
        const distortion = armorDef.armorStats.damageDistortion 
          ? Math.round((1 - armorDef.armorStats.damageDistortion) * 100) : 0;
        return { physical, energy, distortion };
      }
      
      // Fallback estimates
      const armorClass = (armorType || '').toLowerCase();
      switch (armorClass) {
        case 'light': return { physical: 10, energy: 25, distortion: 0 };
        case 'medium': return { physical: 20, energy: 35, distortion: 0 };
        case 'heavy': return { physical: 30, energy: 45, distortion: 0 };
        default: return null;
      }
    })();

    // Armor signature data
    const armorSignatures = {
      infrared: ship.armorSignalInfrared,
      electromagnetic: ship.armorSignalElectromagnetic,
      crossSection: ship.armorSignalCrossSection
    };

    const primaryQD = quantumDrives.find(hp => hp.itemDefinition?.quantumDriveStats);
    const qdStats = primaryQD?.itemDefinition?.quantumDriveStats || null;

    return {
      totalShieldHP,
      totalShieldRegen,
      totalPowerOutput,
      totalCooling,
      totalDPS,
      totalMissileDamage,
      missileCount,
      totalBombDamage,
      bombCount,
      weaponCount: Object.values(weaponsBySize).reduce((sum, w) => sum + w.count, 0),
      turretCount: turrets.length,
      interdiction,
      shieldCount: shields.length,
      powerPlantCount: powerPlants.length,
      coolerCount: coolers.length,
      qdStats,
      // Weapon breakdown
      weaponsBySize,
      missilesBySize,
      bombsBySize,
      // Combat analysis stats
      totalAlpha,
      ballisticDPS,
      energyDPS,
      distortionDPS,
      maxWeaponRange: maxWeaponRange > 0 ? maxWeaponRange : 0,
      minWeaponRange: minWeaponRange !== Infinity ? minWeaponRange : 0,
      allWeapons,
      // Armor stats
      totalArmorHP,
      armorType,
      armorDamageReduction,
      // Power breakdown - ALL systems
      basePowerDraw,
      shieldPowerDraw,
      coolerPowerDraw,
      thrusterPowerDraw,
      qdPowerDraw,
      radarPowerDraw,
      avionicsPowerDraw,
      lifeSupportPowerDraw,
      gravityPowerDraw,
      weaponPowerDraw,
      turretPowerDraw,
      powerUtilization,
      powerSurplus,
    };
  }, [ship]);

  // Grouped hardpoints with proper filtering (same logic as original ShipDetail)
  const groupedHardpoints = useMemo(() => {
    if (!ship?.hardpoints) return { groups: {}, categories: [] };

    const groups: Record<string, ShipHardpoint[]> = {};
    const assigned = new Set<number>();
    
    // Build map of loaded items to their parent hardpoint
    // This captures weapons mounted on turrets, missiles in racks, etc.
    const loadedItemToParent = new Map<string, number>();
    const loadedItemIds = new Set<number>(); // Track IDs of items that are children
    // Track slot names that are nested inside other hardpoints (e.g., hardpoint_class_2 inside hardpoint_weapon_gun_left)
    const nestedSlotNames = new Set<string>();
    
    ship.hardpoints.forEach(hp => {
      if (hp.loadedItems) {
        hp.loadedItems.forEach(item => {
          if (item.itemName) {
            const key = item.itemName.toLowerCase();
            // Map itemName -> parent hardpoint ID
            if (hp.defaultItemName?.toLowerCase() !== key) {
              loadedItemToParent.set(key, hp.id);
            }
          }
          // Also track by loaded item's own ID if available
          if (item.id) {
            loadedItemIds.add(item.id);
          }
          // Track the slot name of this loaded item - any standalone hardpoint with matching slotName is a duplicate
          if (item.slotName) {
            nestedSlotNames.add(item.slotName.toLowerCase());
          }
        });
      }
    });

    // Helper to check if this hardpoint is a child mounted on another hardpoint
    const isChildOfAnotherHardpoint = (hp: ShipHardpoint): boolean => {
      // IMPORTANT: ToolArm-based utility equipment (salvage/mining/tractor arms)
      // can appear both as loadedItems and as standalone hardpoints.
      // Unlike turret gun mounts, we WANT these as standalone entries so the UI
      // can show the proper hierarchy (Arm -> Head/Laser -> Modules).
      const slot = (hp.slotName || '').toLowerCase();
      const defaultName = (hp.defaultItemName || '').toLowerCase();
      const localized = (hp.itemDefinition?.localizedName || '').toLowerCase();
      const type = (hp.itemDefinition?.type || '').toLowerCase();
      const deepAny = (items: any[] | undefined, predicate: (it: any) => boolean): boolean => {
        if (!items || items.length === 0) return false;
        for (const it of items) {
          if (predicate(it)) return true;
          if (it.childAttachments?.length > 0 && deepAny(it.childAttachments, predicate)) return true;
        }
        return false;
      };
      const hasIndustrialChild = deepAny(hp.loadedItems as any[], (it: any) => {
        const t = (it.itemDefinition?.type || '').toLowerCase();
        const n = (it.itemName || '').toLowerCase();
        return t === 'weaponmining' || t === 'salvagehead' || t === 'toolarm' ||
               t === 'miningmodifier' || t === 'salvagemodifier' ||
               n.includes('mining') || n.includes('salvage');
      });
      const isUtilityArm =
        (type === 'toolarm' || type === 'salvagehead' || type === 'weaponmining' || hasIndustrialChild) &&
        (slot.includes('salvage') || slot.includes('mining') || slot.includes('tractor') ||
         defaultName.includes('salvage') || defaultName.includes('mining') || defaultName.includes('tractor') ||
         localized.includes('salvage') || localized.includes('mining') || localized.includes('tractor'));
      if (isUtilityArm) return false;

      // Check if this hardpoint's slotName matches a loadedItem's slotName from another hardpoint
      // This catches duplicates like hardpoint_class_2 that appear both as standalone AND as loadedItems
      if (hp.slotName && nestedSlotNames.has(hp.slotName.toLowerCase())) {
        return true;
      }
      
      // Check if this hardpoint's ID is in the loaded items
      if (loadedItemIds.has(hp.id)) return true;
      
      // Check if this hardpoint's default item name matches a loaded item
      if (hp.defaultItemName) {
        const key = hp.defaultItemName.toLowerCase();
        const parentId = loadedItemToParent.get(key);
        if (parentId !== undefined && parentId !== hp.id) {
          // Exception: FTL drives should always show separately
          const itemDefType = (hp.itemDefinition?.type || '').toLowerCase();
          const hpType = (hp.hardpointType || '').toLowerCase();
          if (itemDefType !== 'jumpdrive' && itemDefType !== 'quantumdrive' && 
              hpType !== 'quantumdrive' && hpType !== 'jumpdrive') {
            return true;
          }
        }
      }
      
      // Check slot naming patterns that indicate child mounts
      const slotName = (hp.slotName || '').toLowerCase();
      // Turret weapon mounts are often named like "turret_gun_hardpoint_gun01" - must have BOTH turret AND hardpoint_gun
      if (slotName.includes('turret') && slotName.includes('hardpoint_gun')) return true;
      // Individual gun positions on multi-gun turrets
      if (slotName.match(/gun_\d+$/) && slotName.includes('turret')) return true;
      
      return false;
    };

    // Helper to check if internal component (should be hidden)
    const isInternalComponent = (hp: ShipHardpoint): boolean => {
      const itemName = (hp.defaultItemName || '').toLowerCase();
      const slotName = (hp.slotName || '').toLowerCase();
      const itemType = (hp.itemDefinition?.type || '').toLowerCase();
      
      if (slotName.match(/^missile_\d+_attach$/) || slotName.match(/^missile_attach_\d+$/)) return true;
      if (itemName.includes('controller_')) return true;
      if (itemName.startsWith('relay_')) return true;
      if (slotName.includes('_mcontroller_') || slotName.includes('_wcontroller_')) return true;
      if (slotName.includes('_aimodule_')) return true;
      if (itemName.includes('weapon_rack') || slotName.includes('weapon_rack')) return true;
      if (itemType === 'cargogrid' || itemType === 'dockingcollar') return true;
      if (itemType === 'lifesupportgenerator' || itemType === 'selfdestruct') return true;
      if (itemType === 'seat' || itemType === 'seatdashboard' || itemType === 'display' || itemType === 'door' || itemType === 'room') return true;
      if (itemType === 'landingsystem' || itemType === 'attachedpart') return true;
      if (slotName.includes('_seat_') || slotName.includes('seat_pilot')) return true;
      if (slotName === 'hardpoint_oc' || itemName.includes('_lighting_oc')) return true;
      if (slotName.includes('remote_turret_interior')) return true;
      if (itemName.includes('hangarcam') || itemName.includes('hangar_cam')) return true;
      // Hide avionics/computers that are incorrectly typed as Weapon
      if (slotName.includes('avionics') || itemName.startsWith('comp_')) return true;
      // Hide weapon controllers
      if (slotName.includes('controller_weapon') || slotName === 'hardpoint_controller_weapon') return true;
      
      return false;
    };

    // Helper: is PDC
    const isPDC = (hp: ShipHardpoint): boolean => {
      const itemName = (hp.defaultItemName || '').toLowerCase();
      const localizedName = (hp.itemDefinition?.localizedName || '').toLowerCase();
      return itemName.includes('turret_pdc') || localizedName.includes('swarm');
    };

    // Helper: is missile turret (turret that launches missiles/torpedoes, should be in missiles section)
    const isMissileTurret = (hp: ShipHardpoint): boolean => {
      const itemName = (hp.defaultItemName || '').toLowerCase();
      const slotName = (hp.slotName || '').toLowerCase();
      // Check if turret has missiles/torpedoes loaded
      if (hp.loadedItems) {
        const hasMissiles = hp.loadedItems.some(item => {
          const loaded = (item.itemName || '').toLowerCase();
          return loaded.startsWith('misl_') || loaded.startsWith('torp_') || loaded.includes('missile') || loaded.includes('torpedo');
        });
        if (hasMissiles) return true;
      }
      // Check item name for missile turret patterns
      if (itemName.includes('turret_msl') || itemName.includes('turret_torp') || itemName.includes('missile_turret')) return true;
      // Check slot name for missile turret patterns
      if (slotName.includes('missile_turret') || slotName.includes('torpedo_turret')) return true;
      return false;
    };

    // Helper: is missile rack or missile-related (includes missile turrets)
    const isMissileRack = (hp: ShipHardpoint): boolean => {
      const itemName = (hp.defaultItemName || '').toLowerCase();
      const slotName = (hp.slotName || '').toLowerCase();
      const itemDefType = (hp.itemDefinition?.type || '').toLowerCase();
      
      if (slotName.includes('camera') || itemName.includes('camera')) return false;
      if (itemName.includes('controller')) return false;
      
      // Check for missile turrets first
      if (isMissileTurret(hp)) return true;
      
      if (itemDefType === 'missilelauncher' || itemDefType === 'missile') return true;
      if (itemName.startsWith('mrck_') || itemName.includes('_mrck_')) return true;
      if (itemName.startsWith('misl_') || itemName.startsWith('torp_')) return true;
      if (slotName.includes('missilebay') || slotName.includes('missile_rack') || slotName.includes('torpedo_bay')) return true;
      
      return false;
    };

    // Helper: is bomb or bomb rack (NOT missile racks that happen to be in a bomb bay)
    const isBomb = (hp: ShipHardpoint): boolean => {
      const itemName = (hp.defaultItemName || '').toLowerCase();
      const slotName = (hp.slotName || '').toLowerCase();
      const itemDefType = (hp.itemDefinition?.type || '').toLowerCase();
      const localizedName = (hp.itemDefinition?.localizedName || '').toLowerCase();
      
      // Exclude missile racks - they go in missiles category even if in a bomb bay
      if (itemName.includes('mrck_') || itemName.includes('missile') || itemName.includes('misl_')) return false;
      if (itemDefType === 'missilelauncher' || itemDefType === 'missilerack') return false;
      
      // Check item definition type
      if (itemDefType === 'bomb') return true;
      
      // Check for bomb patterns in item name (bomb rack or actual bomb)
      if (itemName.startsWith('bomb_')) return true;
      if (itemName.includes('bmbrck_') || itemName.includes('bombrack')) return true;
      
      // Check slot name for bomb system (but item must be bomb-related, not missile)
      if (slotName.includes('bomb_system') && (
        itemName.includes('bomb') || localizedName.includes('bomb') || itemDefType === 'bomb'
      )) return true;
      
      // Check localized name for bombs (not just any item in bomb bay)
      if (localizedName.includes('bomb') && !localizedName.includes('missile')) return true;
      
      // Check if loaded items are actual bombs (not missiles)
      if (hp.loadedItems?.some((item: any) => {
        const liName = (item.itemName || '').toLowerCase();
        const liType = (item.itemDefinition?.type || '').toLowerCase();
        // Must be a bomb, not a missile
        return (liName.startsWith('bomb_') || liType === 'bomb') && 
               !liName.includes('missile') && !liName.includes('mrck_');
      })) return true;
      
      return false;
    };

    // Helper: is tractor beam
    const isTractorBeam = (hp: ShipHardpoint): boolean => {
      const itemName = (hp.defaultItemName || '').toLowerCase();
      const slotName = (hp.slotName || '').toLowerCase();
      const localizedName = (hp.itemDefinition?.localizedName || '').toLowerCase();
      const itemType = (hp.itemDefinition?.type || '').toLowerCase();
      const displayName = (hp.defaultItemDisplayName || '').toLowerCase();

      // Salvage ships often have "tractor" modules attached to salvage heads.
      // Do NOT classify these as Tractor Beams unless the underlying item is actually a tractor beam.
      const isSalvageOrMiningContext =
        slotName.includes('salvage') || slotName.includes('mining') ||
        itemName.includes('salvage') || itemName.includes('mining') ||
        localizedName.includes('salvage') || localizedName.includes('mining') ||
        itemType === 'salvagehead' || itemType === 'weaponmining';
      
      // Check item name patterns
      if (itemName.includes('tractorbeam') || itemName.includes('tractor_beam') || itemName.includes('_tractor')) return true;
      if (itemName.startsWith('trac_')) return true; // TRAC_ prefix for tractor items
      if (itemName.includes('tractor_turret') || itemName.includes('tractor_console')) return true;
      
      // Check slot name for tractor-related slots
      if (slotName.includes('tractor')) return true;
      
      // Check localized/display name
      if (localizedName.includes('tractor')) return true;
      if (displayName.includes('tractor')) return true;
      
      // Check item type
      if (itemType === 'tractorbeam' || itemType.includes('tractor')) return true;
      
      // Check if it's a tractor beam (loaded items / nested)
      const deepAny = (items: any[] | undefined, predicate: (it: any) => boolean): boolean => {
        if (!items || items.length === 0) return false;
        for (const it of items) {
          if (predicate(it)) return true;
          if (it.childAttachments?.length > 0 && deepAny(it.childAttachments, predicate)) return true;
        }
        return false;
      };
      const hasActualTractorItem = deepAny(hp.loadedItems as any[], (item: any) => {
        const liName = (item.itemName || '').toLowerCase();
        const liType = (item.itemDefinition?.type || '').toLowerCase();
        const liDisplay = (item.displayName || '').toLowerCase();
        // Ignore tractor *modules* on salvage heads (e.g., salvage modifiers with _tractor)
        if (liType === 'salvagemodifier' || liType === 'miningmodifier') return false;
        return liType === 'tractorbeam' || liName.includes('tractorbeam') || liName.startsWith('trac_') || liDisplay.includes('tractorbeam');
      });
      if (hasActualTractorItem) return true;

      // If this looks like salvage/mining context, do not treat generic "tractor" strings as a tractor beam.
      if (isSalvageOrMiningContext) return false;
      
      return false;
    };

    // Helper: is countermeasure (chaff, flares, etc.)
    const isCountermeasure = (hp: ShipHardpoint): boolean => {
      const hpType = (hp.hardpointType || '').toLowerCase();
      const itemDef = hp.itemDefinition as any;
      const componentType = (itemDef?.componentType || '').toLowerCase();
      const itemName = (hp.defaultItemName || '').toLowerCase();
      return hpType === 'countermeasure' ||
             componentType === 'weapondefensive' || 
             itemName.includes('_cml_') ||
             itemName.includes('odef_') || 
             itemName.includes('countermeasure') ||
             itemName.includes('chaff') ||
             itemName.includes('noise') ||
             itemName.includes('decoy') ||
             itemName.includes('flare');
    };

    // Helper: is mining or salvage equipment
    const isMiningOrSalvage = (hp: ShipHardpoint): boolean => {
      const itemName = (hp.defaultItemName || '').toLowerCase();
      const itemType = (hp.itemDefinition?.type || '').toLowerCase();
      const localizedName = (hp.itemDefinition?.localizedName || '').toLowerCase();
      const slotName = (hp.slotName || '').toLowerCase();
      const rawTags = (hp.itemDefinition as any)?.tags;
      const itemTags = Array.isArray(rawTags)
        ? rawTags.join(',').toLowerCase()
        : (rawTags ?? '').toString().toLowerCase();
      
      // Exclude internal/filler items - these are ship systems, not equipable items
      const isInternalItem = itemName.includes('filler') || 
                            itemName.includes('internalstorage') ||
                            itemName.includes('internal_storage') ||
                            itemName.includes('disintegration') ||
                            itemName.includes('fieldsupporter') ||
                            itemName.includes('field_supporter') ||
                            itemName.includes('buff_modifier') ||
                            itemName.includes('salvage_buff');
      if (isInternalItem) return false;

      // Never treat modifier modules as standalone category entries.
      // These should appear nested under a mining laser / salvage head.
      if (itemType === 'miningmodifier' || itemType === 'salvagemodifier') return false;

      const hasMiningKeyword =
        slotName.includes('mining') || itemName.includes('mining') || localizedName.includes('mining') ||
        slotName.includes('ore') || itemName.includes('ore') || localizedName.includes('ore');
      const hasSalvageKeyword =
        slotName.includes('salvage') || itemName.includes('salvage') || localizedName.includes('salvage') ||
        slotName.includes('scrape') || itemName.includes('scrape') || localizedName.includes('scrape');

      const isTractorSlotCheck = slotName.includes('tractor') || itemName.includes('tractor') || localizedName.includes('tractor');
      const isSalvageSlotCheck = hasSalvageKeyword;
      const isOrePodOrStorage =
        slotName.includes('pod') || itemName.includes('pod') || localizedName.includes('pod') ||
        itemType === 'container' || itemName.includes('ore_pod') || localizedName.includes('ore pod');

      // Older ShipDetail logic (and LoadoutBuilder data) uses tags to mark mining arms.
      // MOLE mining arms commonly show up as ToolArm + miningMount tag.
      if (itemType === 'toolarm' && itemTags.includes('miningmount')) return true;

      // Strong signals based on the actual item type
      if (itemType === 'weaponmining') return true;
      if (itemType === 'salvagehead') return true;

      // Mirror LoadoutBuilder classification: ToolArm is mining unless it's explicitly salvage/tractor.
      // This fixes MOLE mining arms that don't have "mining" in slot names.
      if (itemType === 'toolarm' && !isSalvageSlotCheck && !isTractorSlotCheck && !isOrePodOrStorage) return true;
      
      // Check for actual mining/salvage equipment
      const isMiningType = itemType === 'weaponmining' || 
                          itemType === 'utilityturret' ||
                          itemType === 'toolarm';
      const isSalvageType = itemType === 'salvagehead' || 
                           itemType === 'toolarm';
      const isMiningSlot = slotName.includes('mining_arm') || slotName.includes('miningarm');
      const isSalvageSlot = slotName.includes('salvage_arm') || slotName.includes('salvagearm');

      const deepAny = (items: any[] | undefined, predicate: (it: any) => boolean): boolean => {
        if (!items || items.length === 0) return false;
        for (const it of items) {
          if (predicate(it)) return true;
          if (it.childAttachments?.length > 0 && deepAny(it.childAttachments, predicate)) return true;
        }
        return false;
      };

      // Loaded items can provide the real signal (e.g., arm hardpoint -> mining laser as child/loaded item)
      if (deepAny(hp.loadedItems as any[], (li: any) => {
        const liType = (li.itemDefinition?.type || '').toLowerCase();
        const liName = (li.itemName || '').toLowerCase();
        const liLocalized = (li.itemDefinition?.localizedName || '').toLowerCase();
        const liRawTags = li.itemDefinition?.tags;
        const liTags = Array.isArray(liRawTags)
          ? liRawTags.join(',').toLowerCase()
          : (liRawTags ?? '').toString().toLowerCase();
        return (
          liType === 'weaponmining' || liType === 'salvagehead' ||
          liType === 'miningmodifier' || liType === 'salvagemodifier' ||
          liTags.includes('miningmount') ||
          liName.includes('mining') || liLocalized.includes('mining') ||
          liName.includes('salvage') || liLocalized.includes('salvage')
        );
      })) {
        return true;
      }
      
      // For ToolArm, check if it's specifically mining or salvage based on slot
      if (itemType === 'toolarm') {
        return isMiningSlot || isSalvageSlot || hasMiningKeyword || hasSalvageKeyword;
      }
      
      return (isMiningType && hasMiningKeyword) ||
             (isSalvageType && (hasSalvageKeyword || itemName.includes('salvage_arm')));
    };

    // Helper: is FTL drive (quantum or jump)
    const isFTLDrive = (hp: ShipHardpoint): boolean => {
      const hpType = (hp.hardpointType || '').toLowerCase();
      const itemType = (hp.itemDefinition?.type || '').toLowerCase();
      const itemName = (hp.defaultItemName || '').toLowerCase();
      const localizedName = (hp.itemDefinition?.localizedName || '').toLowerCase();
      // Exclude fuel tanks
      if (itemType.includes('fueltank') || itemName.includes('fuel') || itemName.includes('tank')) return false;
      // Exclude countermeasures (noise launchers, chaff, etc.)
      if (hpType === 'countermeasure' || itemName.includes('_cml_') || itemName.includes('noise') || 
          itemName.includes('chaff') || itemName.includes('decoy') || itemType.includes('weapondefensive')) return false;
      // Check for quantum drive patterns
      if (itemName.includes('qdrv_') || itemName.includes('jdrv_')) return true;
      // Check type and name patterns
      return hpType.includes('quantum') || hpType.includes('jump') ||
             itemType.includes('quantum') || itemType.includes('jump') ||
             localizedName.includes('quantum') || localizedName.includes('jump');
    };

    // Category definitions
    const categories = [
      { id: 'weapons', name: 'Weapons', types: ['Weapon'], icon: <Crosshair size={20} />, color: 'weapons' },
      { id: 'turrets', name: 'Turrets', types: ['Turret'], icon: <Target size={20} />, color: 'turrets' },
      // EMPs and quantum snares/dampeners — typed from the item they hold.
      { id: 'interdiction', name: 'Interdiction', types: ['EMP', 'QuantumInterdiction'], icon: <Zap size={20} />, color: 'interdiction' },
      { id: 'pdc', name: 'Point Defense', types: [], icon: <ShieldCheck size={20} />, color: 'pdc', customMatch: isPDC },
      { id: 'missiles', name: 'Missiles & Torpedoes', types: ['Missile', 'MissileRack'], icon: <Rocket size={20} />, color: 'missiles', customMatch: isMissileRack },
      { id: 'bombs', name: 'Bombs', types: ['Bomb'], icon: <Flame size={20} />, color: 'bombs', customMatch: isBomb },
      { id: 'countermeasures', name: 'Countermeasures', types: ['Countermeasure', 'WeaponDefensive'], icon: <Disc size={20} />, color: 'countermeasures', customMatch: isCountermeasure },
      { id: 'tractorbeams', name: 'Tractor Beams', types: [], icon: <Anchor size={20} />, color: 'tractorbeams', customMatch: isTractorBeam },
      { id: 'mining', name: 'Mining & Salvage', types: [], icon: <Pickaxe size={20} />, color: 'mining', customMatch: isMiningOrSalvage },
      { id: 'shields', name: 'Shields', types: ['Shield'], icon: <Shield size={20} />, color: 'shields' },
      { id: 'power', name: 'Power Plants', types: ['PowerPlant'], icon: <Battery size={20} />, color: 'power' },
      { id: 'coolers', name: 'Coolers', types: ['Cooler'], icon: <Thermometer size={20} />, color: 'coolers' },
      { id: 'quantum', name: 'FTL Drives', types: ['QuantumDrive', 'JumpDrive', 'JumpModule'], icon: <Sparkles size={20} />, color: 'quantum', customMatch: isFTLDrive },
      { id: 'radar', name: 'Radar & Sensors', types: ['Radar'], icon: <Radio size={20} />, color: 'radar' },
    ];

    // Assign hardpoints to categories
    categories.forEach(category => {
      groups[category.id] = ship.hardpoints!.filter(hp => {
        if (assigned.has(hp.id)) return false;
        if (isInternalComponent(hp)) return false;
        
        // Skip items that are children of another hardpoint (mounted weapons, missiles in racks, etc.)
        if (isChildOfAnotherHardpoint(hp)) return false;

        // Exclude fuel tanks from FTL category
        if (category.id === 'quantum') {
          const itemType = (hp.itemDefinition?.type || '').toLowerCase();
          if (itemType.includes('fueltank')) return false;
        }

        // Exclude PDCs and missile turrets from turrets category
        if (category.id === 'turrets' && isPDC(hp)) return false;
        if (category.id === 'turrets' && isMissileTurret(hp)) return false;
        if (category.id === 'turrets' && isTractorBeam(hp)) return false;
        if (category.id === 'turrets' && isMiningOrSalvage(hp)) return false;

        // For WEAPONS category: exclude items that belong to specialized categories
        // This ensures tractor beams, mining equipment, etc. go to their proper sections
        if (category.id === 'weapons') {
          if (isTractorBeam(hp)) return false;
          if (isMiningOrSalvage(hp)) return false;
          if (isCountermeasure(hp)) return false;
          if (isMissileRack(hp)) return false;
          if (isBomb(hp)) return false;
          if (isPDC(hp)) return false;
          // Exclude nacelles (engine/flight components incorrectly categorized as weapons)
          const itemName = (hp.defaultItemName || '').toLowerCase();
          const localizedName = (hp.itemDefinition?.localizedName || '').toLowerCase();
          if (itemName.includes('nacelle') || localizedName.includes('nacelle')) return false;
        }

        // Standard type matching first
        const typesLower = category.types.map(t => t.toLowerCase());
        const typeMatches = typesLower.length > 0 && (
          typesLower.includes((hp.hardpointType || '').toLowerCase()) ||
          typesLower.includes((hp.itemDefinition?.type || '').toLowerCase())
        );
        
        if (typeMatches) {
          assigned.add(hp.id);
          return true;
        }

        // Custom match function as fallback
        if (category.customMatch && category.customMatch(hp)) {
          assigned.add(hp.id);
          return true;
        }
        
        return false;
      });
    });

    return { groups, categories };
  }, [ship]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <span className={styles.loadingText}>Loading ship data...</span>
        </div>
      </div>
    );
  }

  if (error || !ship) {
    return (
      <div className={styles.page}>
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <Rocket />
          </div>
          <h2 className={styles.emptyTitle}>Ship Not Found</h2>
          <p className={styles.emptyText}>{error || 'Unable to load ship data'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Development Disclaimer */}
      <div className={styles.developmentBanner}>
        <AlertTriangle size={18} />
        <span>This page is still under development. There may be bugs and incomplete information.</span>
      </div>

      {/* Hero Section */}
      <div className={styles.hero}>
        <button className={styles.backButton} onClick={() => navigate('/ship-database')}>
          <ArrowLeft size={18} />
          <span>Back to Ship Database</span>
        </button>
        
        {ship.storeImageUrl && (
          <img 
            src={ship.storeImageUrl} 
            alt={ship.name} 
            className={styles.heroImage}
          />
        )}
        <div className={styles.heroOverlay} />
        
        <div className={styles.heroContent}>
          <div className={styles.identityBadges}>
            {ship.productionStatus && (
              <span className={`${styles.badge} ${styles[ship.productionStatus.toLowerCase().replace(' ', '-')]}`}>
                {ship.productionStatus}
              </span>
            )}
            {ship.size && (
              <span className={styles.badge}>{ship.size}</span>
            )}
            {ship.role && (
              <span className={styles.badge}>{ship.role}</span>
            )}
          </div>
          <h1 className={styles.shipName}>{ship.name}</h1>
          {ship.manufacturer && (
            <p className={styles.manufacturer}>{ship.manufacturer}</p>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className={styles.quickStats}>
        {stats && stats.totalDPS > 0 && (
          <div className={styles.quickStat}>
            <div className={`${styles.quickStatIcon} ${styles.offense}`}>
              <Swords />
            </div>
            <div>
              <div className={styles.quickStatValue}>{formatCompact(stats.totalDPS)}</div>
              <div className={styles.quickStatLabel}>Weapon DPS</div>
              {stats.missileCount > 0 && (
                <div className={styles.quickStatSub}>+{stats.missileCount} missiles</div>
              )}
            </div>
          </div>
        )}
        
        {stats && stats.totalShieldHP > 0 && (
          <div className={styles.quickStat}>
            <div className={`${styles.quickStatIcon} ${styles.defense}`}>
              <Shield />
            </div>
            <div>
              <div className={styles.quickStatValue}>{formatCompact(stats.totalShieldHP)}</div>
              <div className={styles.quickStatLabel}>Shield HP</div>
              <div className={styles.quickStatSub}>{formatCompact(stats.totalShieldRegen)}/s regen</div>
            </div>
          </div>
        )}
        
        {ship.maxSpeed && ship.maxSpeed > 0 && (
          <div className={styles.quickStat}>
            <div className={`${styles.quickStatIcon} ${styles.speed}`}>
              <Gauge />
            </div>
            <div>
              <div className={styles.quickStatValue}>{Math.round(ship.maxSpeed)}</div>
              <div className={styles.quickStatLabel}>Max Speed</div>
              <div className={styles.quickStatSub}>m/s</div>
            </div>
          </div>
        )}
        
        {ship.cargoCapacity !== null && ship.cargoCapacity !== undefined && ship.cargoCapacity > 0 && (
          <div className={styles.quickStat}>
            <div className={`${styles.quickStatIcon} ${styles.cargo}`}>
              <Box />
            </div>
            <div>
              <div className={styles.quickStatValue}>{formatCompact(ship.cargoCapacity)}</div>
              <div className={styles.quickStatLabel}>Cargo SCU</div>
            </div>
          </div>
        )}
        
        {ship.crewMax && ship.crewMax > 0 && (
          <div className={styles.quickStat}>
            <div className={`${styles.quickStatIcon} ${styles.crew}`}>
              <Users />
            </div>
            <div>
              <div className={styles.quickStatValue}>
                {ship.crewMin === ship.crewMax ? ship.crewMax : `${ship.crewMin}-${ship.crewMax}`}
              </div>
              <div className={styles.quickStatLabel}>Crew</div>
            </div>
          </div>
        )}
        
        {stats && stats.totalPowerOutput > 0 && (
          <div className={styles.quickStat}>
            <div className={`${styles.quickStatIcon} ${styles.power}`}>
              <Zap />
            </div>
            <div>
              <div className={styles.quickStatValue}>{formatCompact(stats.totalPowerOutput)}</div>
              <div className={styles.quickStatLabel}>Power Output</div>
              <div className={styles.quickStatSub}>pips</div>
            </div>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className={styles.tabContainer}>
        <div className={styles.tabNav}>
          <button 
            className={`${styles.tab} ${activeTab === 'overview' ? styles.active : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <Settings size={18} />
            Overview
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'combat' ? styles.active : ''}`}
            onClick={() => setActiveTab('combat')}
          >
            <Crosshair size={18} />
            Combat
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'engineering' ? styles.active : ''}`}
            onClick={() => setActiveTab('engineering')}
          >
            <Cpu size={18} />
            Engineering
            <span className={styles.newBadge}>NEW</span>
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'loadout' ? styles.active : ''}`}
            onClick={() => setActiveTab('loadout')}
          >
            <Wrench size={18} />
            Loadout
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'facilities' ? styles.active : ''}`}
            onClick={() => setActiveTab('facilities')}
          >
            <DoorOpen size={18} />
            Facilities
            <span className={styles.newBadge}>NEW</span>
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'reviews' ? styles.active : ''}`}
            onClick={() => setActiveTab('reviews')}
          >
            <Star size={18} />
            Reviews
            {reviewSummary && reviewSummary.totalReviews > 0 && (
              <span className={styles.reviewCount}>{reviewSummary.totalReviews}</span>
            )}
          </button>
        </div>

        {/* Tab Content */}
        <div className={styles.tabContent}>
          {activeTab === 'overview' && (
            <div>
              {/* At a Glance - Quick Summary */}
              <div className={styles.atAGlanceSection}>
                <div className={styles.atAGlanceHeader}>
                  <h3 className={styles.atAGlanceTitle}>At a Glance</h3>
                  <InfoTooltip title="At a Glance">
                    <p>A quick summary of this ship's primary role, crew requirements, and cargo capacity.</p>
                    <p>The <strong>Performance Grades</strong> below give you an instant read on how this ship compares in key areas like speed, range, survivability, and firepower.</p>
                  </InfoTooltip>
                </div>
                <div className={styles.atAGlanceMain}>
                  <div className={styles.atAGlanceRole}>
                    <div className={styles.atAGlanceRoleIcon}>
                      {ship.role?.toLowerCase().includes('combat') || ship.role?.toLowerCase().includes('fighter') ? <Swords size={28} /> :
                       ship.role?.toLowerCase().includes('mining') ? <Pickaxe size={28} /> :
                       ship.role?.toLowerCase().includes('cargo') || ship.role?.toLowerCase().includes('freight') ? <Box size={28} /> :
                       ship.role?.toLowerCase().includes('exploration') ? <Sparkles size={28} /> :
                       ship.role?.toLowerCase().includes('racing') ? <Gauge size={28} /> :
                       ship.role?.toLowerCase().includes('medical') ? <Heart size={28} /> :
                       ship.role?.toLowerCase().includes('salvage') ? <Wrench size={28} /> :
                       ship.role?.toLowerCase().includes('refuel') || ship.role?.toLowerCase().includes('support') ? <Fuel size={28} /> :
                       ship.role?.toLowerCase().includes('passenger') || ship.role?.toLowerCase().includes('touring') ? <Users size={28} /> :
                       ship.role?.toLowerCase().includes('industrial') ? <Settings size={28} /> :
                       <Rocket size={28} />}
                    </div>
                    <div className={styles.atAGlanceRoleText}>
                      <span className={styles.atAGlanceRoleLabel}>{ship.role || 'Multi-Role'}</span>
                      <span className={styles.atAGlanceRoleDesc}>
                        {ship.focus || (ship.role?.toLowerCase().includes('mining') ? 'Resource extraction vessel' :
                         ship.role?.toLowerCase().includes('combat') ? 'Combat-focused spacecraft' :
                         ship.role?.toLowerCase().includes('cargo') ? 'Cargo transport vessel' :
                         'Versatile spacecraft')}
                      </span>
                    </div>
                  </div>
                  
                  {/* Quick Stats Pills */}
                  <div className={styles.quickStatsPills}>
                    {(ship.crewMin || ship.crewMax) && (
                      <div className={styles.quickPill}>
                        <Users size={14} />
                        <span>
                          {ship.crewMin === ship.crewMax || !ship.crewMax 
                            ? `${ship.crewMin || 1} crew` 
                            : `${ship.crewMin || 1}-${ship.crewMax} crew`}
                        </span>
                      </div>
                    )}
                    {ship.cargoCapacity !== undefined && ship.cargoCapacity !== null && (
                      <div className={styles.quickPill}>
                        <Box size={14} />
                        <span>{ship.cargoCapacity > 0 ? `${formatCompact(ship.cargoCapacity)} SCU` : 'No cargo'}</span>
                      </div>
                    )}
                    {ship.size && (
                      <div className={styles.quickPill}>
                        <Gauge size={14} />
                        <span>{ship.size}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Performance Grades */}
                <div className={styles.performanceGrades}>
                  <div className={styles.perfGradesHeader}>
                    <span>Performance Grades</span>
                    <InfoTooltip title="Performance Grades">
                      <p>Quick letter grades (A-F) showing how this ship performs in key areas:</p>
                      <ul>
                        <li><strong>Speed:</strong> How fast in normal flight (SCM)</li>
                        <li><strong>QT Range:</strong> How far on one tank of quantum fuel</li>
                        <li><strong>Survivability:</strong> How much damage it can take</li>
                        <li><strong>Firepower:</strong> How much damage it deals</li>
                      </ul>
                      <p><strong>A</strong> = Excellent, <strong>B</strong> = Good, <strong>C</strong> = Average, <strong>D</strong> = Below Average, <strong>F</strong> = Poor</p>
                    </InfoTooltip>
                  </div>
                  {/* Speed Grade */}
                  {ship.maxSpeed && (() => {
                    // Speed grades based on absolute speed (m/s)
                    // Fighters: 200-280, Medium: 130-200, Large/Capital: 50-130
                    const speed = ship.maxSpeed;
                    const speedGrade = speed >= 250 ? 'A' :
                      speed >= 200 ? 'B' :
                      speed >= 150 ? 'C' :
                      speed >= 100 ? 'D' : 'F';
                    const speedLabel = speed >= 250 ? 'Very Fast' :
                      speed >= 200 ? 'Fast' :
                      speed >= 150 ? 'Average' :
                      speed >= 100 ? 'Slow' : 'Very Slow';
                    const gradeColor = speedGrade === 'A' ? '#22c55e' : 
                      speedGrade === 'B' ? '#84cc16' : 
                      speedGrade === 'C' ? '#eab308' : 
                      speedGrade === 'D' ? '#f97316' : '#ef4444';
                    return (
                      <div className={styles.perfGradeCard}>
                        <div className={styles.perfGradeIcon}><Navigation size={18} /></div>
                        <div className={styles.perfGradeInfo}>
                          <span className={styles.perfGradeName}>Speed</span>
                          <span className={styles.perfGradeValue}>{Math.round(speed)} m/s</span>
                          <span className={styles.perfGradeLabel} style={{ color: gradeColor }}>{speedLabel}</span>
                        </div>
                        <div className={styles.perfGradeLetter} style={{ color: gradeColor }}>{speedGrade}</div>
                      </div>
                    );
                  })()}

                  {/* Range Grade */}
                  {ship.quantumFuelCapacity && stats?.qdStats?.fuelRequirement && (() => {
                    // Calculate approximate number of max-distance jumps
                    // fuelRequirement is fuel per Gm (gigameter)
                    const fuelPerGm = stats.qdStats.fuelRequirement;
                    const maxRangeGm = fuelPerGm > 0 ? ship.quantumFuelCapacity / fuelPerGm : 0;
                    // Stanton to Pyro is roughly 40-50 Gm, average jump ~10 Gm
                    const approxJumps = Math.floor(maxRangeGm / 10);
                    const rangeGrade = approxJumps >= 10 ? 'A' :
                      approxJumps >= 6 ? 'B' :
                      approxJumps >= 3 ? 'C' :
                      approxJumps >= 1 ? 'D' : 'F';
                    const rangeLabel = approxJumps >= 10 ? 'Explorer' :
                      approxJumps >= 6 ? 'Long Range' :
                      approxJumps >= 3 ? 'Medium Range' :
                      approxJumps >= 1 ? 'Short Range' : 'Very Limited';
                    const gradeColor = rangeGrade === 'A' ? '#22c55e' : 
                      rangeGrade === 'B' ? '#84cc16' : 
                      rangeGrade === 'C' ? '#eab308' : 
                      rangeGrade === 'D' ? '#f97316' : '#ef4444';
                    return (
                      <div className={styles.perfGradeCard}>
                        <div className={styles.perfGradeIcon}><Sparkles size={18} /></div>
                        <div className={styles.perfGradeInfo}>
                          <span className={styles.perfGradeName}>QT Range</span>
                          <span className={styles.perfGradeValue}>~{approxJumps} jumps</span>
                          <span className={styles.perfGradeLabel} style={{ color: gradeColor }}>{rangeLabel}</span>
                        </div>
                        <div className={styles.perfGradeLetter} style={{ color: gradeColor }}>{rangeGrade}</div>
                      </div>
                    );
                  })()}

                  {/* Survivability Grade */}
                  {stats && (() => {
                    // Combine shields + armor + hull for survivability
                    const shieldHP = stats.totalShieldHP || 0;
                    const hullHP = ship.hullHealth || 0;
                    const totalHP = shieldHP + hullHP;
                    const survGrade = totalHP >= 500000 ? 'A' :
                      totalHP >= 100000 ? 'B' :
                      totalHP >= 30000 ? 'C' :
                      totalHP >= 10000 ? 'D' : 'F';
                    const survLabel = totalHP >= 500000 ? 'Tank' :
                      totalHP >= 100000 ? 'Heavy' :
                      totalHP >= 30000 ? 'Medium' :
                      totalHP >= 10000 ? 'Light' : 'Fragile';
                    const gradeColor = survGrade === 'A' ? '#22c55e' : 
                      survGrade === 'B' ? '#84cc16' : 
                      survGrade === 'C' ? '#eab308' : 
                      survGrade === 'D' ? '#f97316' : '#ef4444';
                    return (
                      <div className={styles.perfGradeCard}>
                        <div className={styles.perfGradeIcon}><Shield size={18} /></div>
                        <div className={styles.perfGradeInfo}>
                          <span className={styles.perfGradeName}>Survivability</span>
                          <span className={styles.perfGradeValue}>{formatCompact(totalHP)} HP</span>
                          <span className={styles.perfGradeLabel} style={{ color: gradeColor }}>{survLabel}</span>
                        </div>
                        <div className={styles.perfGradeLetter} style={{ color: gradeColor }}>{survGrade}</div>
                      </div>
                    );
                  })()}

                  {/* Firepower Grade (only for combat-capable ships) */}
                  {stats && stats.totalDPS > 0 && (() => {
                    const dps = stats.totalDPS;
                    const fpGrade = dps >= 3000 ? 'A' :
                      dps >= 1500 ? 'B' :
                      dps >= 500 ? 'C' :
                      dps >= 100 ? 'D' : 'F';
                    const fpLabel = dps >= 3000 ? 'Heavy' :
                      dps >= 1500 ? 'Strong' :
                      dps >= 500 ? 'Moderate' :
                      dps >= 100 ? 'Light' : 'Minimal';
                    const gradeColor = fpGrade === 'A' ? '#22c55e' : 
                      fpGrade === 'B' ? '#84cc16' : 
                      fpGrade === 'C' ? '#eab308' : 
                      fpGrade === 'D' ? '#f97316' : '#ef4444';
                    return (
                      <div className={styles.perfGradeCard}>
                        <div className={styles.perfGradeIcon}><Crosshair size={18} /></div>
                        <div className={styles.perfGradeInfo}>
                          <span className={styles.perfGradeName}>Firepower</span>
                          <span className={styles.perfGradeValue}>{formatCompact(dps)} DPS</span>
                          <span className={styles.perfGradeLabel} style={{ color: gradeColor }}>{fpLabel}</span>
                        </div>
                        <div className={styles.perfGradeLetter} style={{ color: gradeColor }}>{fpGrade}</div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Ship Specifications */}
              <div className={styles.statSection}>
                <div className={styles.sectionHeader}>
                  <div className={`${styles.sectionIcon} ${styles.mobility}`}>
                    <Move />
                  </div>
                  <h2 className={styles.sectionTitle}>Ship Specifications</h2>
                  <InfoTooltip title="Ship Specifications">
                    <p>Detailed technical specifications for this ship including:</p>
                    <ul>
                      <li><strong>Dimensions:</strong> Physical size (length, width, height)</li>
                      <li><strong>Mobility:</strong> Flight speeds and quantum travel performance</li>
                      <li><strong>Fuel:</strong> Tank capacities for hydrogen and quantum fuel</li>
                      <li><strong>Operations:</strong> Crew requirements and cargo capacity</li>
                    </ul>
                    <p>Some stats include <em>practical notes</em> showing real-world context (e.g., "~15min across Stanton").</p>
                  </InfoTooltip>
                </div>
                
                <div className={styles.statGrid}>
                  {/* Dimensions Card */}
                  {(ship.length || ship.beam || ship.height) && (
                    <div className={styles.statCard}>
                      <div className={styles.statCardHeader}>
                        <div className={styles.statCardIcon}>
                          <Box />
                        </div>
                        <span className={styles.statCardTitle}>Dimensions</span>
                      </div>
                      <div className={styles.statRows}>
                        {ship.length && (
                          <div className={styles.statRow}>
                            <span className={styles.statLabel}>Length</span>
                            <span className={styles.statValue}>{ship.length}m</span>
                          </div>
                        )}
                        {ship.beam && (
                          <div className={styles.statRow}>
                            <span className={styles.statLabel}>Beam (Width)</span>
                            <span className={styles.statValue}>{ship.beam}m</span>
                          </div>
                        )}
                        {ship.height && (
                          <div className={styles.statRow}>
                            <span className={styles.statLabel}>Height</span>
                            <span className={styles.statValue}>{ship.height}m</span>
                          </div>
                        )}
                        {ship.mass && (
                          <div className={styles.statRow}>
                            <span className={styles.statLabel}>Mass</span>
                            <span className={styles.statValue}>{formatCompact(ship.mass)} kg</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Mobility Card */}
                  <div className={styles.statCard}>
                    <div className={styles.statCardHeader}>
                      <div className={styles.statCardIcon}>
                        <Navigation />
                      </div>
                      <span className={styles.statCardTitle}>Mobility</span>
                    </div>
                    <div className={styles.statRows}>
                      {ship.maxSpeed && (
                        <>
                          <div className={styles.statRow}>
                            <span className={styles.statLabel}>SCM Speed</span>
                            <span className={`${styles.statValue} ${styles.highlight}`}>{Math.round(ship.maxSpeed)} m/s</span>
                          </div>
                        </>
                      )}
                      {stats?.qdStats?.driveSpeed && (
                        <>
                          <div className={styles.statRow}>
                            <span className={styles.statLabel}>Quantum Speed</span>
                            <span className={styles.statValue}>{formatCompact(stats.qdStats.driveSpeed)} m/s</span>
                          </div>
                          {/* Practical: Time to cross Stanton (approx 50 Gm) */}
                          <div className={styles.statRowPractical}>
                            <span className={styles.practicalNote}>
                              ~{Math.round((50000000000 / stats.qdStats.driveSpeed) / 60)}min across Stanton
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Fuel Card */}
                  {(ship.hydrogenFuelCapacity || ship.quantumFuelCapacity) && (
                    <div className={styles.statCard}>
                      <div className={styles.statCardHeader}>
                        <div className={styles.statCardIcon}>
                          <Fuel />
                        </div>
                        <span className={styles.statCardTitle}>Fuel</span>
                      </div>
                      <div className={styles.statRows}>
                        {ship.hydrogenFuelCapacity && (
                          <div className={styles.statRow}>
                            <span className={styles.statLabel}>Hydrogen</span>
                            <span className={styles.statValue}>{formatCompact(ship.hydrogenFuelCapacity)} L</span>
                          </div>
                        )}
                        {ship.quantumFuelCapacity && (
                          <>
                            <div className={styles.statRow}>
                              <span className={styles.statLabel}>Quantum</span>
                              <span className={styles.statValue}>{formatCompact(ship.quantumFuelCapacity)} L</span>
                            </div>
                            {/* Practical: Approximate jump count */}
                            {stats?.qdStats?.fuelRequirement && stats.qdStats.fuelRequirement > 0 && (
                              <div className={styles.statRowPractical}>
                                <span className={styles.practicalNote}>
                                  ~{Math.floor((ship.quantumFuelCapacity / stats.qdStats.fuelRequirement) / 10)} long-range jumps
                                </span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Crew & Cargo Card */}
                  {(ship.crewMin || ship.cargoCapacity) && (
                    <div className={styles.statCard}>
                      <div className={styles.statCardHeader}>
                        <div className={styles.statCardIcon}>
                          <Users />
                        </div>
                        <span className={styles.statCardTitle}>Operations</span>
                      </div>
                      <div className={styles.statRows}>
                        {(ship.crewMin || ship.crewMax) && (
                          <>
                            <div className={styles.statRow}>
                              <span className={styles.statLabel}>Crew</span>
                              <span className={styles.statValue}>
                                {ship.crewMin === ship.crewMax || !ship.crewMax 
                                  ? ship.crewMin || 1
                                  : `${ship.crewMin || 1} - ${ship.crewMax}`}
                              </span>
                            </div>
                            <div className={styles.statRowPractical}>
                              <span className={styles.practicalNote}>
                                {ship.crewMin === 1 || !ship.crewMin ? '✓ Solo-capable' : 
                                 ship.crewMin === 2 ? 'Needs co-pilot' : 
                                 `Requires ${ship.crewMin}+ crew`}
                              </span>
                            </div>
                          </>
                        )}
                        {ship.cargoCapacity !== undefined && ship.cargoCapacity !== null && (
                          <>
                            <div className={styles.statRow}>
                              <span className={styles.statLabel}>Cargo</span>
                              <span className={styles.statValue}>{ship.cargoCapacity} SCU</span>
                            </div>
                            {ship.cargoCapacity > 0 && (
                              <div className={styles.statRowPractical}>
                                <span className={styles.practicalNote}>
                                  {ship.cargoCapacity >= 1000 ? '🏭 Industrial hauler' :
                                   ship.cargoCapacity >= 100 ? '📦 Medium cargo runs' :
                                   ship.cargoCapacity >= 20 ? '📬 Light deliveries' :
                                   ship.cargoCapacity >= 1 ? '🎒 Personal storage' : ''}
                                </span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              {ship.description && (
                <div className={styles.statSection}>
                  <div className={styles.descriptionCard}>
                    <p className={styles.descriptionText}>
                      {ship.description}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'combat' && stats && (
            <div>
              {/* Combat Analysis - Grades and Engagement Profile */}
              {(() => {
                // === COMBAT GRADES ===
                const hullHP = ship.hullHealth || 0;
                const totalEffectiveHP = stats.totalShieldHP + stats.totalArmorHP + hullHP;
                
                // Firepower Grade - based on DPS + missiles + bombs
                // A = 3000+ DPS or heavy torpedo/bomb loadout
                // B = 1500-3000 DPS, C = 500-1500, D = 100-500, F = <100
                const effectiveDPS = stats.totalDPS + (stats.totalMissileDamage / 30) + (stats.totalBombDamage / 30); // Missiles/bombs amortized over 30s
                const firepowerGrade = effectiveDPS >= 3000 ? 'A' :
                  effectiveDPS >= 1500 ? 'B' :
                  effectiveDPS >= 500 ? 'C' :
                  effectiveDPS >= 100 ? 'D' : 'F';
                
                // Survivability Grade - shields + armor + hull
                // Capital ships: 500K+ = A, Medium: 100-500K = B, Light: 30-100K = C
                const survivalGrade = totalEffectiveHP >= 500000 ? 'A' :
                  totalEffectiveHP >= 100000 ? 'B' :
                  totalEffectiveHP >= 30000 ? 'C' :
                  totalEffectiveHP >= 10000 ? 'D' : 'F';
                
                // Overall Combat Rating - weighted average
                const gradeToNum = (g: string) => g === 'A' ? 4 : g === 'B' ? 3 : g === 'C' ? 2 : g === 'D' ? 1 : 0;
                const numToGrade = (n: number) => n >= 3.5 ? 'A' : n >= 2.5 ? 'B' : n >= 1.5 ? 'C' : n >= 0.5 ? 'D' : 'F';
                const overallCombat = numToGrade((gradeToNum(firepowerGrade) * 0.6 + gradeToNum(survivalGrade) * 0.4));
                
                // === ENGAGEMENT PROFILE ===
                const avgWeaponRange = stats.allWeapons.length > 0 
                  ? stats.allWeapons.reduce((sum, w) => sum + w.range, 0) / stats.allWeapons.length 
                  : 0;
                
                // Determine combat style
                const hasTorpedoes = Object.values(stats.missilesBySize).some(m => m.type === 'Torpedo');
                const avgWeaponSize = stats.allWeapons.length > 0
                  ? stats.allWeapons.reduce((sum, w) => sum + w.size, 0) / stats.allWeapons.length
                  : 0;
                
                const combatStyle = hasTorpedoes ? 'Torpedo Boat' :
                  avgWeaponRange > 3000 ? 'Long-Range Striker' :
                  avgWeaponRange > 1500 ? 'Skirmisher' :
                  stats.turretCount > 2 ? 'Turret Platform' :
                  avgWeaponSize >= 4 ? 'Heavy Brawler' :
                  stats.totalDPS > 2000 ? 'Gunship' :
                  'Close-Range Brawler';
                
                // Time-to-Kill estimates (vs typical fighter with 4K shields, 2K armor, 1.5K hull)
                const typicalFighterHP = 7500;
                const ttk = stats.totalDPS > 0 ? typicalFighterHP / stats.totalDPS : Infinity;
                
                // Time-to-Die estimate (how long this ship survives vs 1500 DPS fighter)
                const typicalFighterDPS = 1500;
                const ttd = totalEffectiveHP / typicalFighterDPS;
                
                // Shield recovery time (0 to full)
                const shieldRecoveryTime = stats.totalShieldRegen > 0 
                  ? stats.totalShieldHP / stats.totalShieldRegen 
                  : 0;
                
                // Damage type breakdown percentages
                const totalTypedDPS = stats.ballisticDPS + stats.energyDPS + stats.distortionDPS;
                const ballisticPct = totalTypedDPS > 0 ? Math.round((stats.ballisticDPS / totalTypedDPS) * 100) : 0;
                const energyPct = totalTypedDPS > 0 ? Math.round((stats.energyDPS / totalTypedDPS) * 100) : 0;
                const distortionPct = totalTypedDPS > 0 ? Math.round((stats.distortionDPS / totalTypedDPS) * 100) : 0;
                
                return (
                  <>
                    {/* Combat Analysis Header */}
                    <div className={styles.statSection}>
                      <div className={styles.sectionHeader}>
                        <div className={`${styles.sectionIcon} ${styles.combat}`}>
                          <Swords />
                        </div>
                        <h2 className={styles.sectionTitle}>Combat Analysis</h2>
                        <InfoTooltip title="Combat Analysis">
                          <p>This section grades your ship's combat effectiveness using letter grades (A-F).</p>
                          <p><strong>Combat Rating</strong> is a weighted score combining Firepower (60%) and Survivability (40%).</p>
                          <p>The <strong>Combat Style</strong> (e.g., "Close-Range Brawler") is determined by your weapon loadout, range, and defensive capabilities.</p>
                        </InfoTooltip>
                      </div>
                      
                      {/* Combat Grades */}
                      <div className={styles.combatGradeSection}>
                        <div className={styles.combatOverallGrade}>
                          <div className={`${styles.gradeCircleLarge} ${styles[`grade${overallCombat}`]}`}>
                            {overallCombat}
                          </div>
                          <div className={styles.overallGradeLabel}>
                            <span className={styles.gradeTitle}>Combat Rating</span>
                            <span className={styles.combatStyle}>{combatStyle}</span>
                          </div>
                        </div>
                        
                        <div className={styles.combatSubGrades}>
                          <div className={styles.combatSubGradeCard}>
                            <Crosshair className={styles.subGradeIcon} />
                            <span className={`${styles.subGradeLetter} ${styles[`grade${firepowerGrade}`]}`}>{firepowerGrade}</span>
                            <span className={styles.subGradeLabel}>
                              Firepower
                              <InfoTooltip title="Firepower Grade">
                                <p>Grades your ship's damage output based on total DPS plus missile damage (amortized over 30s).</p>
                                <ul>
                                  <li><strong>A:</strong> 3,000+ DPS - Heavy combat ships</li>
                                  <li><strong>B:</strong> 1,500-3,000 DPS - Combat-capable</li>
                                  <li><strong>C:</strong> 500-1,500 DPS - Light combat</li>
                                  <li><strong>D:</strong> 100-500 DPS - Minimal offense</li>
                                  <li><strong>F:</strong> Under 100 DPS - Non-combat</li>
                                </ul>
                              </InfoTooltip>
                            </span>
                            <span className={styles.subGradeDetail}>{formatCompact(stats.totalDPS)} DPS</span>
                          </div>
                          <div className={styles.combatSubGradeCard}>
                            <Shield className={styles.subGradeIcon} />
                            <span className={`${styles.subGradeLetter} ${styles[`grade${survivalGrade}`]}`}>{survivalGrade}</span>
                            <span className={styles.subGradeLabel}>
                              Survivability
                              <InfoTooltip title="Survivability Grade">
                                <p>Grades your ship's ability to take damage, based on total Effective HP (Shields + Armor + Hull).</p>
                                <ul>
                                  <li><strong>A:</strong> 500K+ EHP - Capital-class</li>
                                  <li><strong>B:</strong> 100K-500K EHP - Heavy ships</li>
                                  <li><strong>C:</strong> 30K-100K EHP - Medium ships</li>
                                  <li><strong>D:</strong> 10K-30K EHP - Light ships</li>
                                  <li><strong>F:</strong> Under 10K EHP - Very fragile</li>
                                </ul>
                              </InfoTooltip>
                            </span>
                            <span className={styles.subGradeDetail}>{formatCompact(totalEffectiveHP)} EHP</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Engagement Profile */}
                      <div className={styles.engagementProfile}>
                        <h3 className={styles.engagementTitle}>
                          <Target size={18} />
                          Engagement Profile
                          <InfoTooltip title="Engagement Profile">
                            <p>Practical combat metrics to help you understand how this ship performs in a fight.</p>
                            <p>All estimates assume typical combat conditions against a reference light fighter.</p>
                          </InfoTooltip>
                        </h3>
                        <div className={styles.engagementGrid}>
                          <div className={styles.engagementStat}>
                            <span className={styles.engagementLabel}>
                              Effective Range
                              <InfoTooltip title="Effective Range">
                                <p>The distance at which your weapons are effective, from minimum to maximum range.</p>
                                <p>Stay within this range for optimal damage output.</p>
                              </InfoTooltip>
                            </span>
                            <span className={styles.engagementValue}>
                              {stats.maxWeaponRange > 0 ? `${formatCompact(stats.minWeaponRange)}-${formatCompact(stats.maxWeaponRange)}m` : 'N/A'}
                            </span>
                          </div>
                          <div className={styles.engagementStat}>
                            <span className={styles.engagementLabel}>
                              Alpha Strike
                              <InfoTooltip title="Alpha Strike">
                                <p>Total burst damage if you fire ALL weapons, missiles, and bombs simultaneously.</p>
                                <p>A high alpha strike is great for ambushes and opening engagements.</p>
                              </InfoTooltip>
                            </span>
                            <span className={styles.engagementValue}>{formatCompact(stats.totalAlpha + stats.totalMissileDamage + stats.totalBombDamage)} dmg</span>
                          </div>
                          <div className={styles.engagementStat}>
                            <span className={styles.engagementLabel}>
                              Time to Kill*
                              <InfoTooltip title="Time to Kill (TTK)">
                                <p>How long it takes this ship to destroy a typical light fighter (7,500 EHP) with sustained fire.</p>
                                <p><strong>Green (≤5s):</strong> Excellent - quick kills</p>
                                <p><strong>Blue (5-10s):</strong> Good - competitive</p>
                                <p><strong>White (10s+):</strong> Slow - may struggle in dogfights</p>
                              </InfoTooltip>
                            </span>
                            <span className={`${styles.engagementValue} ${ttk <= 5 ? styles.excellent : ttk <= 10 ? styles.good : ''}`}>
                              {ttk === Infinity ? 'N/A' : `~${ttk.toFixed(1)}s`}
                            </span>
                          </div>
                          <div className={styles.engagementStat}>
                            <span className={styles.engagementLabel}>
                              Time to Die*
                              <InfoTooltip title="Time to Die (TTD)">
                                <p>How long this ship survives against a typical light fighter dealing 1,500 DPS.</p>
                                <p><strong>Green (30s+):</strong> Very tanky</p>
                                <p><strong>Blue (15-30s):</strong> Decent survivability</p>
                                <p><strong>White (&lt;15s):</strong> Fragile - avoid prolonged fights</p>
                              </InfoTooltip>
                            </span>
                            <span className={`${styles.engagementValue} ${ttd >= 30 ? styles.excellent : ttd >= 15 ? styles.good : ''}`}>
                              ~{ttd.toFixed(0)}s
                            </span>
                          </div>
                        </div>
                        <div className={styles.engagementFootnote}>
                          *vs typical light fighter (7.5K EHP, 1.5K DPS)
                        </div>
                      </div>
                    </div>

                    {/* Damage Type Breakdown */}
                    {totalTypedDPS > 0 && (
                      <div className={styles.statSection}>
                        <div className={styles.sectionHeader}>
                          <div className={`${styles.sectionIcon} ${styles.damage}`}>
                            <Flame />
                          </div>
                          <h2 className={styles.sectionTitle}>Damage Profile</h2>
                          <InfoTooltip title="Damage Profile">
                            <p>Shows the breakdown of your weapon damage types:</p>
                            <ul>
                              <li><strong>Ballistic:</strong> Physical projectiles. Effective vs armor but requires ammo.</li>
                              <li><strong>Energy:</strong> Laser/plasma weapons. Unlimited ammo, better vs shields.</li>
                              <li><strong>Distortion:</strong> Disables ships without destroying them. Great for captures/bounties.</li>
                            </ul>
                            <p>A mixed loadout is often ideal for versatility.</p>
                          </InfoTooltip>
                        </div>
                        
                        <div className={styles.damageBreakdown}>
                          <div className={styles.damageBar}>
                            {ballisticPct > 0 && (
                              <div 
                                className={`${styles.damageSegment} ${styles.ballistic}`}
                                style={{ width: `${ballisticPct}%` }}
                                title={`Ballistic: ${ballisticPct}%`}
                              />
                            )}
                            {energyPct > 0 && (
                              <div 
                                className={`${styles.damageSegment} ${styles.energy}`}
                                style={{ width: `${energyPct}%` }}
                                title={`Energy: ${energyPct}%`}
                              />
                            )}
                            {distortionPct > 0 && (
                              <div 
                                className={`${styles.damageSegment} ${styles.distortion}`}
                                style={{ width: `${distortionPct}%` }}
                                title={`Distortion: ${distortionPct}%`}
                              />
                            )}
                          </div>
                          <div className={styles.damageLegend}>
                            {ballisticPct > 0 && (
                              <div className={styles.legendItem}>
                                <span className={`${styles.legendDot} ${styles.ballistic}`} />
                                <span className={styles.legendLabel}>Ballistic</span>
                                <span className={styles.legendValue}>{ballisticPct}%</span>
                              </div>
                            )}
                            {energyPct > 0 && (
                              <div className={styles.legendItem}>
                                <span className={`${styles.legendDot} ${styles.energy}`} />
                                <span className={styles.legendLabel}>Energy</span>
                                <span className={styles.legendValue}>{energyPct}%</span>
                              </div>
                            )}
                            {distortionPct > 0 && (
                              <div className={styles.legendItem}>
                                <span className={`${styles.legendDot} ${styles.distortion}`} />
                                <span className={styles.legendLabel}>Distortion</span>
                                <span className={styles.legendValue}>{distortionPct}%</span>
                              </div>
                            )}
                          </div>
                          <div className={styles.damageInsight}>
                            {ballisticPct > 60 && <span>⚠️ Ammo dependent - watch your rounds</span>}
                            {energyPct > 80 && <span>✓ No ammo concerns - unlimited sustained fire</span>}
                            {distortionPct > 30 && <span>⚡ Can disable before destroy - good for captures</span>}
                            {ballisticPct > 0 && energyPct > 0 && Math.abs(ballisticPct - energyPct) < 20 && (
                              <span>✓ Mixed loadout - effective vs all armor types</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Survivability Stack */}
                    <div className={styles.statSection}>
                      <div className={styles.sectionHeader}>
                        <div className={`${styles.sectionIcon} ${styles.defense}`}>
                          <ShieldCheck />
                        </div>
                        <h2 className={styles.sectionTitle}>Survivability Stack</h2>
                        <InfoTooltip title="Survivability Stack">
                          <p>Your ship's defensive layers, shown in the order damage passes through them:</p>
                          <ol>
                            <li><strong>Shields:</strong> First line of defense. Regenerates over time!</li>
                            <li><strong>Armor:</strong> Reduces incoming damage by a percentage. Doesn't regenerate.</li>
                            <li><strong>Hull:</strong> Your ship's structure. When this hits 0, you're dead.</li>
                          </ol>
                          <p><strong>EHP (Effective HP)</strong> combines all three layers into one number for easy comparison.</p>
                        </InfoTooltip>
                      </div>
                      
                      <div className={styles.survivalStack}>
                        <div className={styles.stackVisualization}>
                          {/* Shield Layer */}
                          <div className={`${styles.stackLayer} ${styles.shieldLayer}`}>
                            <div className={styles.stackLayerHeader}>
                              <Shield size={16} />
                              <span>Shields</span>
                              <InfoTooltip title="Shields">
                                <p>Energy barriers that absorb damage. Key stats:</p>
                                <ul>
                                  <li><strong>HP:</strong> Total shield capacity</li>
                                  <li><strong>Regen:</strong> HP recovered per second</li>
                                </ul>
                                <p>Shields regenerate constantly (with a delay after taking damage). They're your renewable defense!</p>
                              </InfoTooltip>
                            </div>
                            <div className={styles.stackLayerStats}>
                              <span className={styles.stackHP}>{formatCompact(stats.totalShieldHP)} HP</span>
                              <span className={styles.stackRegen}>+{formatCompact(stats.totalShieldRegen)}/s</span>
                            </div>
                            <div className={styles.stackLayerBar}>
                              <div 
                                className={styles.stackBarFill}
                                style={{ width: `${Math.min((stats.totalShieldHP / totalEffectiveHP) * 100, 100)}%` }}
                              />
                            </div>
                          </div>
                          
                          {/* Armor Layer */}
                          <div className={`${styles.stackLayer} ${styles.armorLayer}`}>
                            <div className={styles.stackLayerHeader}>
                              <ShieldCheck size={16} />
                              <span>Armor {stats.armorType && `(${stats.armorType})`}</span>
                              <InfoTooltip title="Armor">
                                <p>Physical plating that reduces damage. New in Star Citizen Alpha 4.5!</p>
                                <ul>
                                  <li><strong>HP:</strong> How much damage armor can absorb</li>
                                  <li><strong>Damage Reduction:</strong> % of damage blocked (physical vs energy)</li>
                                </ul>
                                <p>Armor classes: Light → Medium → Heavy (more protection, more weight)</p>
                              </InfoTooltip>
                            </div>
                            <div className={styles.stackLayerStats}>
                              <span className={styles.stackHP}>{formatCompact(stats.totalArmorHP)} HP</span>
                              {stats.armorDamageReduction && (
                                <span className={styles.stackDR}>
                                  -{stats.armorDamageReduction.physical}% phys / -{stats.armorDamageReduction.energy}% energy
                                </span>
                              )}
                            </div>
                            <div className={styles.stackLayerBar}>
                              <div 
                                className={styles.stackBarFill}
                                style={{ width: `${Math.min((stats.totalArmorHP / totalEffectiveHP) * 100, 100)}%` }}
                              />
                            </div>
                          </div>
                          
                          {/* Hull Layer */}
                          <div className={`${styles.stackLayer} ${styles.hullLayer}`}>
                            <div className={styles.stackLayerHeader}>
                              <Box size={16} />
                              <span>Hull</span>
                              <InfoTooltip title="Hull">
                                <p>Your ship's structural integrity - the last line of defense.</p>
                                <p>When hull HP reaches 0, your ship is destroyed. Unlike shields, hull damage does not regenerate without repairs.</p>
                              </InfoTooltip>
                            </div>
                            <div className={styles.stackLayerStats}>
                              <span className={styles.stackHP}>{formatCompact(hullHP)} HP</span>
                            </div>
                            <div className={styles.stackLayerBar}>
                              <div 
                                className={styles.stackBarFill}
                                style={{ width: `${Math.min((hullHP / totalEffectiveHP) * 100, 100)}%` }}
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className={styles.survivalInsights}>
                          <div className={styles.insightItem}>
                            <Clock size={14} />
                            <span>Shield recovery: {shieldRecoveryTime > 0 ? `${shieldRecoveryTime.toFixed(0)}s` : 'N/A'} (0 → full)</span>
                          </div>
                          <div className={styles.insightItem}>
                            <Heart size={14} />
                            <span>Total EHP: {formatCompact(totalEffectiveHP)}</span>
                          </div>
                          {stats.shieldCount > 1 && (
                            <div className={styles.insightItem}>
                              <CheckCircle size={14} />
                              <span>{stats.shieldCount} shield generators - redundancy if one fails</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Detailed Weapon & Missile Cards (existing) */}
                    <div className={styles.statSection}>
                      <div className={styles.sectionHeader}>
                        <div className={`${styles.sectionIcon} ${styles.weapons}`}>
                          <Crosshair />
                        </div>
                        <h2 className={styles.sectionTitle}>Weapon Details</h2>
                      </div>
                      
                      <div className={styles.statGrid}>
                  {/* Interdiction / EMP gear */}
                  {stats.interdiction.length > 0 && (
                    <div className={styles.statCard}>
                      <div className={styles.statCardHeader}>
                        <div className={styles.statCardIcon}>
                          <Zap />
                        </div>
                        <span className={styles.statCardTitle}>Interdiction</span>
                      </div>
                      <div className={styles.statRows}>
                        {stats.interdiction.map((hp, i) => (
                          <div key={i} className={styles.statRow}>
                            <span className={styles.statLabel}>
                              {hp.hardpointType === 'EMP' ? 'EMP' : 'Quantum Dampener'}
                            </span>
                            <span className={styles.statValue}>
                              {hp.itemDefinition?.localizedName || hp.defaultItemName || 'Unknown'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Offensive Stats */}
                  <div className={styles.statCard}>
                    <div className={styles.statCardHeader}>
                      <div className={styles.statCardIcon}>
                        <Crosshair />
                      </div>
                      <span className={styles.statCardTitle}>Weapons</span>
                    </div>
                    <div className={styles.statRows}>
                      {stats.totalDPS > 0 && (
                        <div className={styles.statWithBar}>
                          <div className={styles.statBarHeader}>
                            <span className={styles.statBarLabel}>Total DPS</span>
                            <span className={styles.statBarValue}>{formatCompact(stats.totalDPS)}</span>
                          </div>
                          <div className={styles.statBar}>
                            <div 
                              className={`${styles.statBarFill} ${styles.red}`}
                              style={{ width: `${Math.min((stats.totalDPS / 5000) * 100, 100)}%` }}
                            />
                          </div>
                        </div>
                      )}
                      {/* Weapon breakdown by size - with names like missiles */}
                      {Object.keys(stats.weaponsBySize).length > 0 && (
                        <div className={styles.breakdownList}>
                          {Object.entries(stats.weaponsBySize)
                            .sort(([a], [b]) => Number(b) - Number(a))
                            .map(([size, data]) => {
                              // Get display name - prefer actual weapon name over generic "weapon"
                              // Filter out bad names like controller_weapon or placeholders
                              const validNames = (data.names || []).filter(n => 
                                n && 
                                !n.toLowerCase().includes('controller_weapon') &&
                                !n.toLowerCase().includes('placeholder') &&
                                !n.toLowerCase().includes('unknown')
                              );
                              const displayName = validNames.length > 0 
                                ? (validNames.length === 1 ? validNames[0] : validNames.join(' / '))
                                : 'Weapon';
                              
                              return (
                                <div key={size} className={styles.breakdownItem}>
                                  <span className={styles.breakdownLabel}>
                                    <span className={`${styles.sizeBadge} ${styles[`s${size}`]}`}>S{size}</span>
                                    {data.count}x {displayName}
                                  </span>
                                  <span className={styles.breakdownValue}>{formatCompact(data.dps)} DPS</span>
                                </div>
                              );
                            })
                          }
                        </div>
                      )}
                      {stats.turretCount > 0 && (
                        <div className={styles.statRow}>
                          <span className={styles.statLabel}>Turrets</span>
                          <span className={styles.statValue}>{stats.turretCount}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Missiles with breakdown */}
                  {stats.missileCount > 0 && (
                    <div className={styles.statCard}>
                      <div className={styles.statCardHeader}>
                        <div className={styles.statCardIcon}>
                          <Rocket />
                        </div>
                        <span className={styles.statCardTitle}>Missiles & Torpedoes</span>
                      </div>
                      <div className={styles.statRows}>
                        <div className={styles.statRow}>
                          <span className={styles.statLabel}>Total Payload</span>
                          <span className={`${styles.statValue} ${styles.highlight}`}>{stats.missileCount}</span>
                        </div>
                        <div className={styles.statRow}>
                          <span className={styles.statLabel}>Total Damage</span>
                          <span className={styles.statValue}>{formatCompact(stats.totalMissileDamage)}</span>
                        </div>
                        {/* Missile breakdown by size */}
                        {Object.keys(stats.missilesBySize).length > 0 && (
                          <div className={styles.breakdownList}>
                            {Object.entries(stats.missilesBySize)
                              .sort(([a], [b]) => Number(b) - Number(a))
                              .map(([size, data]) => (
                                <div key={size} className={`${styles.breakdownItem} ${data.type === 'Torpedo' ? styles.torpedo : ''}`}>
                                  <span className={styles.breakdownLabel}>
                                    <span className={`${styles.sizeBadge} ${styles[`s${size}`]}`}>S{size}</span>
                                    {data.count}x {data.type}{data.count > 1 ? 's' : ''}
                                  </span>
                                  <span className={styles.breakdownValue}>{formatCompact(data.damage)} dmg</span>
                                </div>
                              ))
                            }
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Bombs with breakdown */}
                  {stats.bombCount > 0 && (
                    <div className={styles.statCard}>
                      <div className={styles.statCardHeader}>
                        <div className={`${styles.statCardIcon} ${styles.bombs}`}>
                          <Flame />
                        </div>
                        <span className={styles.statCardTitle}>Bombs</span>
                      </div>
                      <div className={styles.statRows}>
                        <div className={styles.statRow}>
                          <span className={styles.statLabel}>Total Payload</span>
                          <span className={`${styles.statValue} ${styles.highlight}`}>{stats.bombCount}</span>
                        </div>
                        <div className={styles.statRow}>
                          <span className={styles.statLabel}>Total Damage</span>
                          <span className={styles.statValue}>{formatCompact(stats.totalBombDamage)}</span>
                        </div>
                        {/* Bomb breakdown by size */}
                        {Object.keys(stats.bombsBySize).length > 0 && (
                          <div className={styles.breakdownList}>
                            {Object.entries(stats.bombsBySize)
                              .sort(([a], [b]) => Number(b) - Number(a))
                              .map(([size, data]) => (
                                <div key={size} className={`${styles.breakdownItem} ${styles.bomb}`}>
                                  <span className={styles.breakdownLabel}>
                                    <span className={`${styles.sizeBadge} ${styles[`s${size}`]}`}>S{size}</span>
                                    {data.count}x Bomb{data.count > 1 ? 's' : ''}
                                  </span>
                                  <span className={styles.breakdownValue}>{formatCompact(data.damage)} dmg</span>
                                </div>
                              ))
                            }
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* Engineering Tab - Redesigned for actionable insights */}
          {activeTab === 'engineering' && stats && (
            <div>
              <div className={styles.statSection}>
                <div className={styles.sectionHeader}>
                  <div className={`${styles.sectionIcon} ${styles.engineering}`}>
                    <Cpu />
                  </div>
                  <h2 className={styles.sectionTitle}>Engineering</h2>
                  <InfoTooltip title="Engineering">
                    <p>This section analyzes how well your ship's internal systems work together.</p>
                    <p>The <strong>Loadout Balance</strong> grade shows if your components are properly matched - a powerful weapon on an underpowered ship won't perform well!</p>
                    <p>Key systems graded:</p>
                    <ul>
                      <li><strong>Power:</strong> Is your power plant right-sized?</li>
                      <li><strong>Cooling:</strong> Can you sustain combat without overheating?</li>
                      <li><strong>Shields:</strong> How fast do shields regenerate?</li>
                    </ul>
                  </InfoTooltip>
                  <span className={styles.newInScBadge}>New in SC Alpha 4.5</span>
                </div>

                {/* Loadout Balance Analysis */}
                {(() => {
                  // === POWER GRADE ===
                  // Grade based on power plant efficiency - is it oversized or undersized for the loadout?
                  // Ideal utilization is 40-70% - leaves headroom but isn't wasteful
                  // We use base power draw which represents idle - in combat, draw increases significantly
                  // So a ship at 1% idle is actually fine, but we want to know if there's room for upgrades
                  const powerHeadroom = 100 - stats.powerUtilization;
                  
                  // Calculate power efficiency score based on total power draw vs output
                  // Ships should have some headroom but not excessive (indicates oversized PP)
                  // Grade A = perfect balance, Grade C = stock/typical, Grade F = deficit
                  const powerEfficiency = stats.totalPowerOutput > 0 
                    ? (stats.basePowerDraw / stats.totalPowerOutput) * 100 
                    : 0;
                  
                  // Power grade: Deficit is F, then grade on how much headroom relative to draw
                  // More draw = ship can support more systems = better
                  const powerGrade = stats.powerSurplus < 0 ? 'F' :
                    powerEfficiency >= 60 ? 'A' :  // Using 60-100% of capacity = well optimized
                    powerEfficiency >= 40 ? 'B' :  // Using 40-60% = good balance
                    powerEfficiency >= 20 ? 'C' :  // Using 20-40% = typical/stock
                    powerEfficiency >= 5 ? 'D' :   // Using 5-20% = oversized power plant
                    'D';  // Very low utilization = significantly oversized
                  
                  // === COOLING GRADE ===
                  // Cooling effectiveness - more coolers = better sustained combat
                  // Grade based on cooler count and total capacity relative to power
                  // More coolers = redundancy + higher capacity
                  const coolingPerKW = stats.totalPowerOutput > 0 
                    ? stats.totalCooling / (stats.totalPowerOutput / 1000) 
                    : 0;
                  
                  // Also factor in cooler count for redundancy
                  const coolerRedundancy = stats.coolerCount >= 3 ? 1.2 : 
                    stats.coolerCount === 2 ? 1.0 : 
                    stats.coolerCount === 1 ? 0.7 : 0;
                  
                  const coolingScore = coolingPerKW * coolerRedundancy;
                  
                  const coolingGrade = stats.totalCooling === 0 ? 'F' :
                    coolingScore >= 80 ? 'A' :
                    coolingScore >= 55 ? 'B' :
                    coolingScore >= 35 ? 'C' :
                    coolingScore >= 15 ? 'D' : 'F';
                  
                  // === SHIELD GRADE ===
                  // Shield effectiveness - HP and regen time combined
                  // Faster regen = better, but also consider total HP pool
                  const regenTime = stats.totalShieldRegen > 0 
                    ? stats.totalShieldHP / stats.totalShieldRegen 
                    : Infinity;
                  
                  // Tighter thresholds - stock ships should be B/C
                  const shieldGrade = stats.totalShieldHP === 0 ? 'N/A' :
                    regenTime <= 15 ? 'A' :  // Very fast regen
                    regenTime <= 25 ? 'B' :  // Good regen
                    regenTime <= 40 ? 'C' :  // Average regen (most stock ships)
                    regenTime <= 60 ? 'D' :  // Slow regen
                    'F';                      // Very slow
                  
                  // Determine bottleneck
                  const gradeToNum = (g: string) => g === 'A' ? 5 : g === 'B' ? 4 : g === 'C' ? 3 : g === 'D' ? 2 : g === 'F' ? 1 : 0;
                  const grades = [
                    { system: 'Power', grade: powerGrade, num: gradeToNum(powerGrade), detail: `${Math.round(powerEfficiency)}% utilized` },
                    { system: 'Cooling', grade: coolingGrade, num: gradeToNum(coolingGrade), detail: `${formatCompact(stats.totalCooling)}/s` },
                    { system: 'Shields', grade: shieldGrade, num: gradeToNum(shieldGrade), detail: stats.totalShieldHP > 0 ? `${Math.round(regenTime)}s regen` : 'None' },
                  ].filter(g => g.grade !== 'N/A');
                  
                  const bottleneck = grades.length > 0 
                    ? grades.reduce((min, g) => g.num < min.num ? g : min, grades[0])
                    : null;
                  
                  // Overall grade - weighted average, pulled down harder by bottleneck
                  const avgGrade = grades.length > 0 
                    ? grades.reduce((sum, g) => sum + g.num, 0) / grades.length
                    : 0;
                  // Bottleneck pulls down the overall score
                  const bottleneckPenalty = bottleneck && bottleneck.num <= 2 ? 0.5 : 0;
                  const adjustedAvg = avgGrade - bottleneckPenalty;
                  
                  const overallGrade = adjustedAvg >= 4.5 ? 'A' :
                    adjustedAvg >= 3.5 ? 'B' :
                    adjustedAvg >= 2.5 ? 'C' :
                    adjustedAvg >= 1.5 ? 'D' : 'F';
                  
                  const gradeColor = (g: string) => 
                    g === 'A' ? '#22c55e' : 
                    g === 'B' ? '#84cc16' : 
                    g === 'C' ? '#eab308' : 
                    g === 'D' ? '#f97316' : 
                    g === 'F' ? '#ef4444' : '#64748b';

                  return (
                    <>
                      {/* Overall Loadout Grade */}
                      <div className={styles.loadoutGradeSection}>
                        <div className={styles.overallGradeCard}>
                          <div className={styles.overallGradeLabel}>
                            Loadout Balance
                            <InfoTooltip title="Loadout Balance">
                              <p>An overall grade for how well your ship's components work together.</p>
                              <p>This considers Power, Cooling, and Shield efficiency. A bottleneck in any system will pull down the overall grade.</p>
                              <p><strong>A:</strong> Optimized loadout</p>
                              <p><strong>B-C:</strong> Typical/stock configuration</p>
                              <p><strong>D-F:</strong> Significant issues to address</p>
                            </InfoTooltip>
                          </div>
                          <div className={styles.overallGrade} style={{ color: gradeColor(overallGrade) }}>
                            {overallGrade}
                          </div>
                          <div className={styles.overallGradeDesc}>
                            {overallGrade === 'A' ? 'Excellent - well optimized components' :
                             overallGrade === 'B' ? 'Good - minor improvements possible' :
                             overallGrade === 'C' ? 'Average - typical stock configuration' :
                             overallGrade === 'D' ? 'Below Average - consider upgrades' :
                             'Poor - significant issues detected'}
                          </div>
                        </div>
                        
                        {/* Individual System Grades */}
                        <div className={styles.systemGradesRow}>
                          <div className={styles.systemGradeCard}>
                            <Zap size={18} className={styles.systemGradeIcon} />
                            <div className={styles.systemGradeInfo}>
                              <span className={styles.systemGradeName}>
                                Power
                                <InfoTooltip title="Power Grade">
                                  <p>How well your power plant matches your system needs.</p>
                                  <p><strong>% utilized</strong> = power draw / power output</p>
                                  <ul>
                                    <li><strong>A (60%+):</strong> Well-optimized, using capacity efficiently</li>
                                    <li><strong>B (40-60%):</strong> Good balance with headroom</li>
                                    <li><strong>C (20-40%):</strong> Typical stock config</li>
                                    <li><strong>D/F (&lt;20%):</strong> Oversized power plant (wasted potential)</li>
                                  </ul>
                                  <p><strong>F</strong> also means power deficit - you need a bigger power plant!</p>
                                </InfoTooltip>
                              </span>
                              <span className={styles.systemGradeDetail}>{Math.round(powerEfficiency)}% utilized</span>
                            </div>
                            <div className={styles.systemGradeLetter} style={{ color: gradeColor(powerGrade) }}>
                              {powerGrade}
                            </div>
                          </div>
                          
                          <div className={styles.systemGradeCard}>
                            <Thermometer size={18} className={styles.systemGradeIcon} />
                            <div className={styles.systemGradeInfo}>
                              <span className={styles.systemGradeName}>
                                Cooling
                                <InfoTooltip title="Cooling Grade">
                                  <p>How well your coolers can handle heat from weapons and systems.</p>
                                  <p>Better cooling = longer sustained fire in combat without overheating.</p>
                                  <ul>
                                    <li><strong>Multiple coolers:</strong> Provides redundancy if one fails</li>
                                    <li><strong>Higher capacity:</strong> More heat dissipation per second</li>
                                  </ul>
                                  <p>If you overheat often in combat, upgrade your coolers!</p>
                                </InfoTooltip>
                              </span>
                              <span className={styles.systemGradeDetail}>{stats.coolerCount}× ({formatCompact(stats.totalCooling)}/s)</span>
                            </div>
                            <div className={styles.systemGradeLetter} style={{ color: gradeColor(coolingGrade) }}>
                              {coolingGrade}
                            </div>
                          </div>
                          
                          <div className={styles.systemGradeCard}>
                            <Shield size={18} className={styles.systemGradeIcon} />
                            <div className={styles.systemGradeInfo}>
                              <span className={styles.systemGradeName}>
                                Shields
                                <InfoTooltip title="Shield Grade">
                                  <p>How effective your shield regeneration is.</p>
                                  <p><strong>"X seconds to full"</strong> = time to regenerate from 0 to max shields.</p>
                                  <ul>
                                    <li><strong>A (≤15s):</strong> Very fast - can recover mid-fight</li>
                                    <li><strong>B (15-25s):</strong> Good - reasonable recovery</li>
                                    <li><strong>C (25-40s):</strong> Average - typical stock shields</li>
                                    <li><strong>D-F (40s+):</strong> Slow - vulnerable during recovery</li>
                                  </ul>
                                </InfoTooltip>
                              </span>
                              <span className={styles.systemGradeDetail}>
                                {stats.totalShieldHP > 0 ? `${Math.round(regenTime)}s to full` : 'None'}
                              </span>
                            </div>
                            <div className={styles.systemGradeLetter} style={{ color: gradeColor(shieldGrade) }}>
                              {shieldGrade}
                            </div>
                          </div>
                        </div>

                        {/* Bottleneck Indicator */}
                        {bottleneck && bottleneck.num < 4 && (
                          <div className={styles.bottleneckAlert}>
                            <AlertTriangle size={18} />
                            <div>
                              <strong>Bottleneck: {bottleneck.system}</strong>
                              <span>
                                {bottleneck.system === 'Power' && stats.powerSurplus < 0 
                                  ? `Power plant can't handle current draw. ${formatCompact(Math.abs(stats.powerSurplus))} pips short.`
                                  : bottleneck.system === 'Power' 
                                  ? `Only ${powerHeadroom}% power headroom. Upgrades may cause brownouts.`
                                  : bottleneck.system === 'Cooling' && stats.totalCooling === 0
                                  ? 'No active coolers detected. Ship will overheat in combat.'
                                  : bottleneck.system === 'Cooling'
                                  ? `Cooling capacity may limit sustained fire. Consider upgrading coolers.`
                                  : bottleneck.system === 'Shields'
                                  ? `Shield regen is slow (${Math.round(regenTime)}s). Vulnerable during recovery.`
                                  : 'This system may limit combat effectiveness.'
                                }
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}

                {/* Two-column layout for Power and Thermal budgets */}
                <div className={styles.engineeringBudgetGrid}>
                  {/* Power Budget - Left Column */}
                  <div className={styles.budgetCard}>
                    <div className={styles.budgetHeader}>
                      <Zap size={20} />
                      <span>Power Budget</span>
                      <InfoTooltip title="Power Budget">
                        <p>Shows your power plant's output and how much is being used by ship systems.</p>
                        <ul>
                          <li><strong>Output:</strong> Total power your plant generates</li>
                          <li><strong>% utilized at idle:</strong> Power used when not in combat</li>
                          <li><strong>Headroom:</strong> Extra power available for upgrades</li>
                        </ul>
                        <p>The breakdown shows which systems consume the most power. Shields and thrusters are typically the biggest draws.</p>
                      </InfoTooltip>
                    </div>
                    
                    <div className={styles.budgetMain}>
                      <div className={styles.budgetMainRow}>
                        <span>Output</span>
                        <span className={styles.budgetMainValue}>{formatCompact(stats.totalPowerOutput)} pips</span>
                      </div>
                      <div className={styles.budgetProgressContainer}>
                        <div className={styles.budgetProgressBar}>
                          <div 
                            className={`${styles.budgetProgressFill} ${
                              stats.powerUtilization > 100 ? styles.critical :
                              stats.powerUtilization > 85 ? styles.warning :
                              stats.powerUtilization > 60 ? styles.caution :
                              styles.good
                            }`}
                            style={{ width: `${Math.min(stats.powerUtilization, 100)}%` }}
                          />
                        </div>
                        <span className={styles.budgetProgressLabel}>{stats.powerUtilization}% utilized at idle</span>
                      </div>
                      <div className={`${styles.budgetHeadroom} ${stats.powerSurplus >= 0 ? styles.positive : styles.negative}`}>
                        <span>Headroom</span>
                        <span>{stats.powerSurplus >= 0 ? '+' : ''}{formatCompact(stats.powerSurplus)} pips available for upgrades</span>
                      </div>
                    </div>

                    <div className={styles.budgetBreakdown}>
                      <div className={styles.budgetBreakdownTitle}>Top Power Consumers</div>
                      {(() => {
                        // Sort power consumers and show top ones
                        const consumers = [
                          { name: 'Shields', value: stats.shieldPowerDraw, icon: Shield, color: 'var(--gradient-shield)' },
                          { name: 'Thrusters', value: stats.thrusterPowerDraw, icon: Navigation, color: 'var(--gradient-speed)' },
                          { name: 'Weapons', value: stats.weaponPowerDraw + stats.turretPowerDraw, icon: Crosshair, color: 'var(--gradient-fire)' },
                          { name: 'Quantum Drive', value: stats.qdPowerDraw, icon: Sparkles, color: 'linear-gradient(90deg, #8b5cf6 0%, #6d28d9 100%)' },
                          { name: 'Coolers', value: stats.coolerPowerDraw, icon: Thermometer, color: 'linear-gradient(90deg, #06b6d4 0%, #0891b2 100%)' },
                          { name: 'Radar', value: stats.radarPowerDraw, icon: Radio, color: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)' },
                          { name: 'Avionics', value: stats.avionicsPowerDraw, icon: Cpu, color: 'linear-gradient(90deg, #64748b 0%, #475569 100%)' },
                          { name: 'Life Support', value: stats.lifeSupportPowerDraw, icon: Wind, color: 'linear-gradient(90deg, #10b981 0%, #059669 100%)' },
                          { name: 'Gravity', value: stats.gravityPowerDraw, icon: Anchor, color: 'linear-gradient(90deg, #ec4899 0%, #be185d 100%)' },
                        ].filter(c => c.value > 0).sort((a, b) => b.value - a.value);
                        
                        const maxValue = consumers[0]?.value || 1;
                        
                        return consumers.slice(0, 5).map((consumer, idx) => {
                          const Icon = consumer.icon;
                          const percent = stats.basePowerDraw > 0 
                            ? Math.round((consumer.value / stats.basePowerDraw) * 100) 
                            : 0;
                          return (
                            <div key={consumer.name} className={styles.budgetItem}>
                              <div className={styles.budgetItemLabel}>
                                <Icon size={14} />
                                <span>{consumer.name}</span>
                              </div>
                              <div className={styles.budgetItemBar}>
                                <div 
                                  className={styles.budgetItemFill}
                                  style={{ 
                                    width: `${(consumer.value / maxValue) * 100}%`,
                                    background: consumer.color
                                  }}
                                />
                              </div>
                              <span className={styles.budgetItemValue}>
                                {percent}%
                                <span className={styles.budgetItemWatts}>({formatCompact(consumer.value)} pips)</span>
                              </span>
                            </div>
                          );
                        });
                      })()}
                    </div>

                    {/* Component summary */}
                    <div className={styles.budgetComponents}>
                      <div className={styles.componentBadge}>
                        <Battery size={14} />
                        <span>{stats.powerPlantCount}× Power Plant</span>
                      </div>
                    </div>
                  </div>

                  {/* Thermal Budget - Right Column */}
                  <div className={styles.budgetCard}>
                    <div className={styles.budgetHeader}>
                      <Thermometer size={20} />
                      <span>Thermal Budget</span>
                      <InfoTooltip title="Thermal Budget">
                        <p><strong>Cooling Capacity</strong> determines how quickly your ship can dissipate heat from weapons fire and system operation.</p>
                        <p><strong>Grading:</strong></p>
                        <ul>
                          <li><strong>A:</strong> High headroom with multiple coolers</li>
                          <li><strong>B:</strong> Good capacity for sustained combat</li>
                          <li><strong>C:</strong> Adequate for standard operations</li>
                          <li><strong>D/F:</strong> Risk of overheating in combat</li>
                        </ul>
                        <p>Ships with single coolers may overheat during extended engagements. Multiple coolers provide both capacity and redundancy.</p>
                      </InfoTooltip>
                    </div>
                    
                    <div className={styles.budgetMain}>
                      <div className={styles.budgetMainRow}>
                        <span>Cooling Capacity</span>
                        <span className={styles.budgetMainValue}>{formatCompact(stats.totalCooling)}/s</span>
                      </div>
                      <div className={styles.budgetProgressContainer}>
                        <div className={styles.budgetProgressBar}>
                          <div 
                            className={`${styles.budgetProgressFill} ${styles.cooling}`}
                            style={{ width: '100%' }}
                          />
                        </div>
                        <span className={styles.budgetProgressLabel}>
                          {stats.coolerCount > 0 
                            ? `${stats.coolerCount} cooler${stats.coolerCount > 1 ? 's' : ''} active`
                            : 'No active coolers'
                          }
                        </span>
                      </div>
                    </div>

                    <div className={styles.thermalInsight}>
                      {stats.totalCooling === 0 ? (
                        <div className={styles.insightWarning}>
                          <span className={styles.insightIcon}>⚠️</span>
                          <div>
                            <strong>No cooling detected</strong>
                            <p>Ship may overheat quickly during combat. Check cooler hardpoints.</p>
                          </div>
                        </div>
                      ) : stats.coolerCount === 1 ? (
                        <div className={styles.insightCaution}>
                          <span className={styles.insightIcon}>💡</span>
                          <div>
                            <strong>Single cooler configuration</strong>
                            <p>Extended combat may cause heat buildup. Consider adding backup cooling.</p>
                          </div>
                        </div>
                      ) : (
                        <div className={styles.insightGood}>
                          <span className={styles.insightIcon}>✓</span>
                          <div>
                            <strong>Thermal capacity looks good</strong>
                            <p>Multiple coolers provide redundancy and sustained combat capability.</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Shield regeneration time (relates to thermal/power) */}
                    {stats.totalShieldHP > 0 && stats.totalShieldRegen > 0 && (
                      <div className={styles.thermalShieldRegen}>
                        <div className={styles.regenHeader}>
                          <Shield size={16} />
                          <span>Shield Recovery</span>
                          <InfoTooltip title="Shield Recovery">
                            <p><strong>Shield HP</strong> is the total capacity across all shield faces.</p>
                            <p><strong>Regen Rate</strong> is how fast shields restore per second while not taking damage.</p>
                            <p><strong>Time to Full</strong> estimates how long to regenerate from 0% to 100%.</p>
                            <p>In combat, shields only regenerate after a brief delay (hardening time) once damage stops. Larger shields take longer to recover but provide more protection.</p>
                          </InfoTooltip>
                        </div>
                        <div className={styles.regenStats}>
                          <div className={styles.regenStat}>
                            <span className={styles.regenLabel}>Total HP</span>
                            <span className={styles.regenValue}>{formatCompact(stats.totalShieldHP)}</span>
                          </div>
                          <div className={styles.regenStat}>
                            <span className={styles.regenLabel}>Regen Rate</span>
                            <span className={styles.regenValue}>{formatCompact(stats.totalShieldRegen)}/s</span>
                          </div>
                          <div className={styles.regenStat}>
                            <span className={styles.regenLabel}>Time to Full</span>
                            <span className={styles.regenValue}>~{Math.round(stats.totalShieldHP / stats.totalShieldRegen)}s</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Component summary */}
                    <div className={styles.budgetComponents}>
                      <div className={styles.componentBadge}>
                        <Thermometer size={14} />
                        <span>{stats.coolerCount}× Cooler</span>
                      </div>
                      {stats.shieldCount > 0 && (
                        <div className={styles.componentBadge}>
                          <Shield size={14} />
                          <span>{stats.shieldCount}× Shield</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Power Pip Management - 4.5 Dawn of Engineering */}
                {ship && ship.hardpoints && (
                  <PowerManagementPanel
                    ship={ship}
                    hardpoints={ship.hardpoints}
                    readOnly={true}
                    onPowerAllocationsChange={setPowerAllocations}
                    weaponContent={
                      <EmbeddedWeaponList
                        ship={ship}
                        hardpoints={ship.hardpoints}
                        readOnly={true}
                      />
                    }
                  />
                )}

                {/* Stealth & Detection Profile Panel */}
                {ship && ship.hardpoints && (
                  <StealthProfilePanel
                    ship={ship}
                    hardpoints={ship.hardpoints}
                  />
                )}

                {/* Fuel & Range */}
                {(ship.hydrogenFuelCapacity || ship.quantumFuelCapacity) && (
                  <div className={styles.engineeringFuelSection}>
                    <div className={styles.fuelHeader}>
                      <Fuel size={18} />
                      <span>Fuel Reserves & Range</span>
                      <InfoTooltip title="Fuel Reserves & Range">
                        <p><strong>Hydrogen Fuel</strong> powers your main thrusters and afterburner. Used for normal flight, combat maneuvering, and atmospheric operations.</p>
                        <p><strong>Quantum Fuel</strong> is consumed during quantum travel between locations. Larger tanks allow longer jumps without refueling.</p>
                        <p>Ships with low fuel capacity may need to refuel more frequently at stations or via Starlifter refueling ships.</p>
                      </InfoTooltip>
                    </div>
                    <div className={styles.fuelGrid}>
                      {ship.hydrogenFuelCapacity && (
                        <div className={styles.fuelCard}>
                          <div className={styles.fuelIcon}>
                            <Flame size={24} />
                          </div>
                          <div className={styles.fuelInfo}>
                            <span className={styles.fuelType}>Hydrogen</span>
                            <span className={styles.fuelAmount}>{formatCompact(ship.hydrogenFuelCapacity)} L</span>
                            <span className={styles.fuelUse}>Thrusters & Afterburner</span>
                          </div>
                        </div>
                      )}
                      {ship.quantumFuelCapacity && (
                        <div className={styles.fuelCard}>
                          <div className={styles.fuelIcon}>
                            <Sparkles size={24} />
                          </div>
                          <div className={styles.fuelInfo}>
                            <span className={styles.fuelType}>Quantum</span>
                            <span className={styles.fuelAmount}>{formatCompact(ship.quantumFuelCapacity)} L</span>
                            <span className={styles.fuelUse}>Quantum Travel</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'loadout' && ship.hardpoints && groupedHardpoints.categories && (
            <div>
              <div className={styles.statSection}>
                <div className={styles.sectionHeader}>
                  <div className={`${styles.sectionIcon} ${styles.combat}`}>
                    <Wrench />
                  </div>
                  <h2 className={styles.sectionTitle}>Equipment Loadout</h2>
                  <span className={styles.sectionSubtitle}>
                    {Object.values(groupedHardpoints.groups).reduce((sum, arr) => sum + arr.length, 0)} components
                  </span>
                </div>

                {/* Dynamic categories */}
                {groupedHardpoints.categories.map(category => {
                  const categoryItems = groupedHardpoints.groups[category.id] || [];
                  if (categoryItems.length === 0) return null;

                  return (
                    <div key={category.id} className={styles.categoryAccordion}>
                      <div 
                        className={styles.categoryHeader}
                        onClick={() => toggleCategory(category.id)}
                      >
                        <div className={`${styles.categoryIcon} ${styles[category.color]}`}>
                          {category.icon}
                        </div>
                        <span className={styles.categoryTitle}>{category.name}</span>
                        <span className={styles.categoryCount}>{categoryItems.length}</span>
                        <ChevronDown 
                          className={`${styles.categoryChevron} ${expandedCategories.has(category.id) ? styles.open : ''}`}
                          size={20}
                        />
                      </div>
                      {expandedCategories.has(category.id) && (
                        <div className={styles.categoryContent}>
                          <div className={styles.componentGrid}>
                            {categoryItems.map((hp, idx) => {
                              // Calculate effective size for display
                              const isTurret = category.id === 'turrets';
                              const isWeapon = category.id === 'weapons';
                              
                              // SIZE LOGIC:
                              // - Turrets: Use the HARDPOINT/TURRET size (hp.size or hp.itemDefinition.size)
                              // - Weapons with mount: Use the MOUNT size (loadedItems[0].itemDefinition.size)
                              // - Direct weapons: Use the WEAPON size (loadedItems[0].itemDefinition.size)
                              // - Missiles/Torpedoes: Use the largest missile size OR rack size
                              // - Other categories: Use hardpoint size
                              let effectiveSize = hp.size || hp.itemDefinition?.size || 1;
                              const isMissile = category.id === 'missiles';
                              
                              if (isWeapon && hp.loadedItems && hp.loadedItems.length > 0) {
                                // For weapons, get size from the mount or direct weapon
                                const firstItem = hp.loadedItems[0];
                                if (firstItem.itemDefinition?.size) {
                                  effectiveSize = firstItem.itemDefinition.size;
                                }
                              }
                              
                              // For missiles/torpedoes: use the largest missile size, or fall back to rack itemDef size
                              if (isMissile && hp.loadedItems && hp.loadedItems.length > 0) {
                                let maxMissileSize = 0;
                                hp.loadedItems.forEach((item: any) => {
                                  const missileSize = item.itemDefinition?.size || 0;
                                  if (missileSize > maxMissileSize) {
                                    maxMissileSize = missileSize;
                                  }
                                });
                                if (maxMissileSize > 0) {
                                  effectiveSize = maxMissileSize;
                                } else if (hp.itemDefinition?.size) {
                                  effectiveSize = hp.itemDefinition.size;
                                }
                              }
                              // For turrets, keep the hardpoint size (the turret itself)

                              // Get FTL type badge for quantum drives
                              const ftlType = category.id === 'quantum' ? (
                                (hp.itemDefinition?.type || '').toLowerCase().includes('jump') ? 'Jump' : 'Quantum'
                              ) : null;

                              // Helper to format a raw item name into display name
                              const formatItemName = (name: string): string => {
                                if (!name) return '';
                                // Remove common prefixes and format
                                return name
                                  .replace(/^mrck_/i, 'Missile Rack ')
                                  .replace(/^misl_/i, '')
                                  .replace(/^torp_/i, '')
                                  .replace(/_/g, ' ')
                                  .replace(/\s+/g, ' ')
                                  .trim()
                                  .split(' ')
                                  .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                  .join(' ');
                              };

                              // Get display name for component - handles weapons, missiles, and other categories
                              const getDisplayName = () => {
                                const isMissile = category.id === 'missiles';
                                const isMining = category.id === 'mining';
                                
                                // TURRETS: Show the TURRET name (from hardpoint), NOT the guns
                                // Hierarchy: Hardpoint (turret) -> loadedItems (guns)
                                if (isTurret) {
                                  // Turret name comes from the hardpoint itself
                                  if (hp.itemDefinition?.localizedName && !hp.itemDefinition.localizedName.includes('PLACEHOLDER')) {
                                    return hp.itemDefinition.localizedName;
                                  }
                                  if (hp.defaultItemName) {
                                    return formatItemName(hp.defaultItemName);
                                  }
                                  // Fallback based on slot name
                                  const slotName = (hp.slotName || '').toLowerCase();
                                  if (slotName.includes('remote')) return 'Remote Turret';
                                  if (slotName.includes('manned')) return 'Manned Turret';
                                  return 'Turret';
                                }
                                
                                // WEAPONS: Show MOUNT name (from defaultItemName) if we have loadedItems (guns)
                                // Structure A: hp.defaultItemName=Mount, hp.loadedItems[0]=Gun (Aurora, Gladius, etc.)
                                // Structure B: hp.defaultItemName=Mount, hp.loadedItems[0]=Mount, childAttachments[0]=Gun (Polaris turrets)
                                if (isWeapon && hp.loadedItems && hp.loadedItems.length > 0) {
                                  const firstItem = hp.loadedItems[0];
                                  
                                  // Check if loadedItem has childAttachments (Structure B - nested mount->gun)
                                  if (firstItem.childAttachments && firstItem.childAttachments.length > 0) {
                                    // Show the mount from loadedItems
                                    if (firstItem.itemDefinition?.localizedName) {
                                      return firstItem.itemDefinition.localizedName;
                                    }
                                    if (firstItem.displayName) {
                                      return firstItem.displayName;
                                    }
                                    if (firstItem.itemName) {
                                      return formatItemName(firstItem.itemName);
                                    }
                                    return 'Weapon Mount';
                                  } else {
                                    // Structure A: loadedItem IS the gun, mount is in defaultItemName
                                    // Check if defaultItemName looks like a mount/gimbal
                                    const defaultName = (hp.defaultItemName || '').toLowerCase();
                                    const isMount = defaultName.includes('gimbal') || defaultName.includes('mount') || 
                                                   defaultName.includes('turret') || defaultName.includes('varipuck');
                                    
                                    if (isMount) {
                                      // Show the mount name from defaultItemName
                                      if (hp.itemDefinition?.localizedName) {
                                        return hp.itemDefinition.localizedName;
                                      }
                                      if (hp.defaultItemName) {
                                        return formatItemName(hp.defaultItemName);
                                      }
                                      return 'Weapon Mount';
                                    } else {
                                      // Direct weapon attachment (no mount) - show the weapon name
                                      // Special case: If firstItem is a weapon PART (Barrel, FiringMechanism, etc.),
                                      // the actual weapon name is on the hardpoint, not in loadedItems (e.g., Ares SF7E Cannon)
                                      const firstItemType = (firstItem.itemDefinition?.type || '').toLowerCase();
                                      const firstItemSubType = (firstItem.itemDefinition?.subType || '').toLowerCase();
                                      const isWeaponPart = firstItemType === 'weaponattachment' || 
                                                          ['barrel', 'firingmechanism', 'powerarray', 'ventilation'].includes(firstItemSubType);
                                      
                                      if (isWeaponPart && hp.itemDefinition?.localizedName && !hp.itemDefinition.localizedName.includes('PLACEHOLDER')) {
                                        return hp.itemDefinition.localizedName;
                                      }
                                      // Original logic: try loadedItems
                                      if (firstItem.itemDefinition?.localizedName) {
                                        return firstItem.itemDefinition.localizedName;
                                      }
                                      if (firstItem.displayName) {
                                        return firstItem.displayName;
                                      }
                                      if (firstItem.itemName) {
                                        return formatItemName(firstItem.itemName);
                                      }
                                    }
                                  }
                                }
                                
                                // For mining/salvage: show the actual tool/laser/head from loadedItems if available
                                // Skip modules (SalvageModifier/MiningModifier) - find the actual head/laser
                                if (isMining && hp.loadedItems && hp.loadedItems.length > 0) {
                                  // Find the first item that is NOT a module (head/laser, not modifier)
                                  const headOrLaser = hp.loadedItems.find((item: any) => {
                                    const itemType = (item.itemDefinition?.type || '').toLowerCase();
                                    const itemNameLower = (item.itemName || '').toLowerCase();
                                    // Skip modules - they should not be the card title
                                    const isModule = itemType === 'salvagemodifier' || itemType === 'miningmodifier' ||
                                                    itemNameLower.includes('salvage_modifier') || itemNameLower.includes('mining_modifier') ||
                                                    itemNameLower.includes('_scraper') || itemNameLower.includes('_tractor') ||
                                                    itemNameLower.includes('abrade') || itemNameLower.includes('cinch') ||
                                                    itemNameLower.includes('surge') || itemNameLower.includes('stampede');
                                    return !isModule;
                                  });
                                  
                                  if (headOrLaser) {
                                    // Check childAttachments first (mining arm -> mining laser)
                                    if (headOrLaser.childAttachments && headOrLaser.childAttachments.length > 0) {
                                      // Find a child that's a head/laser, not a module
                                      const tool = headOrLaser.childAttachments.find((child: any) => {
                                        const childType = (child.itemDefinition?.type || '').toLowerCase();
                                        return childType === 'salvagehead' || childType === 'weaponmining' || 
                                               childType.includes('laser') || childType.includes('head');
                                      }) || headOrLaser.childAttachments[0];
                                      const toolName = tool.itemDefinition?.localizedName || tool.displayName;
                                      if (toolName && !toolName.includes('PLACEHOLDER')) {
                                        return toolName;
                                      }
                                    }
                                    // Direct tool on loadedItem
                                    const toolName = headOrLaser.itemDefinition?.localizedName || headOrLaser.displayName;
                                    if (toolName && !toolName.includes('PLACEHOLDER')) {
                                      return toolName;
                                    }
                                  }
                                }
                                
                                // For missiles, show the rack name (not count)
                                if (isMissile) {
                                  // Use rack's localizedName if available, otherwise format the item name
                                  if (hp.itemDefinition?.localizedName) {
                                    return hp.itemDefinition.localizedName;
                                  }
                                  if (hp.defaultItemName) {
                                    const formatted = formatItemName(hp.defaultItemName);
                                    if (formatted) return formatted;
                                  }
                                  return 'Missile Rack';
                                }
                                
                                // For bombs, show the bomb rack/console name
                                const isBombCategory = category.id === 'bombs';
                                if (isBombCategory) {
                                  // Use rack's localizedName if available, otherwise format the item name
                                  if (hp.itemDefinition?.localizedName) {
                                    return hp.itemDefinition.localizedName;
                                  }
                                  if (hp.defaultItemName) {
                                    const formatted = formatItemName(hp.defaultItemName);
                                    if (formatted) return formatted;
                                  }
                                  return 'Bomb Rack';
                                }
                                
                                // For tractor beams, ensure correct naming (database may have wrong names like "Mining Arm")
                                const isTractorCategory = category.id === 'tractorbeams';
                                if (isTractorCategory) {
                                  const itemName = (hp.defaultItemName || '').toLowerCase();
                                  const slotName = (hp.slotName || '').toLowerCase();
                                  
                                  // SRV's tractor beam arm is incorrectly named "Mining Arm" in database
                                  // Detect by item/slot name patterns and provide correct name
                                  if (itemName.includes('tractorbeamarm') || slotName.includes('tractorbeamarm')) {
                                    return 'Tractor Beam Arm';
                                  }
                                  
                                  // If localized name doesn't contain "mining" (misclassified), use it
                                  const localizedName = hp.itemDefinition?.localizedName || '';
                                  if (localizedName && !localizedName.toLowerCase().includes('mining') && !localizedName.includes('PLACEHOLDER')) {
                                    return localizedName;
                                  }
                                  
                                  // Format from item name
                                  if (hp.defaultItemName) {
                                    const formatted = formatItemName(hp.defaultItemName);
                                    if (formatted) return formatted;
                                  }
                                  
                                  return 'Tractor Beam';
                                }
                                
                                // Standard fallback: localizedName > formatted defaultItemName > defaultItemDisplayName
                                // Skip placeholder names AND "Unknown Item"
                                if (hp.itemDefinition?.localizedName && 
                                    !hp.itemDefinition.localizedName.includes('PLACEHOLDER') &&
                                    hp.itemDefinition.localizedName !== 'Unknown Item') {
                                  return hp.itemDefinition.localizedName;
                                }
                                if (hp.defaultItemName) {
                                  const formatted = formatItemName(hp.defaultItemName);
                                  if (formatted && formatted !== 'Unknown Item' && !formatted.includes('PLACEHOLDER')) {
                                    return formatted;
                                  }
                                }
                                // For mining/salvage with no good name, show the hardpoint type
                                if (isMining) {
                                  const slotName = (hp.slotName || '').toLowerCase();
                                  if (slotName.includes('salvage')) {
                                    // Generate better name for salvage arms
                                    if (slotName.includes('left')) return 'Salvage Arm (Left)';
                                    if (slotName.includes('right')) return 'Salvage Arm (Right)';
                                    return 'Salvage Arm';
                                  }
                                  if (slotName.includes('mining')) {
                                    if (slotName.includes('left')) return 'Mining Arm (Left)';
                                    if (slotName.includes('right')) return 'Mining Arm (Right)';
                                    return 'Mining Arm';
                                  }
                                  return 'Utility Slot';
                                }
                                return hp.defaultItemDisplayName || 'Empty Slot';
                              };

                              // Get manufacturer - from turret for turrets, from mount for weapons, from missile for missiles
                              const getManufacturer = () => {
                                let mfg: string | null | undefined;
                                const isMissile = category.id === 'missiles';
                                const isMining = category.id === 'mining';
                                
                                // For TURRETS: Get manufacturer from the TURRET (hardpoint item)
                                if (isTurret) {
                                  mfg = hp.itemDefinition?.manufacturer;
                                }
                                
                                // For WEAPONS: Get manufacturer from the mount or direct weapon
                                if (isWeapon && hp.loadedItems && hp.loadedItems.length > 0) {
                                  const firstItem = hp.loadedItems[0];
                                  mfg = firstItem.itemDefinition?.manufacturer;
                                }
                                
                                // For mining/salvage, get manufacturer from the tool
                                if (isMining && hp.loadedItems && hp.loadedItems.length > 0) {
                                  const firstLoaded = hp.loadedItems[0];
                                  if (firstLoaded.childAttachments && firstLoaded.childAttachments.length > 0) {
                                    mfg = firstLoaded.childAttachments[0].itemDefinition?.manufacturer;
                                  } else {
                                    mfg = firstLoaded.itemDefinition?.manufacturer;
                                  }
                                }
                                
                                // For missiles, get manufacturer from loaded missile
                                if (isMissile && hp.loadedItems && hp.loadedItems.length > 0) {
                                  const firstMissile = hp.loadedItems[0];
                                  if (firstMissile.itemDefinition?.manufacturer) {
                                    mfg = firstMissile.itemDefinition.manufacturer;
                                  }
                                }
                                
                                // For bombs, get manufacturer from the bomb rack or loaded bomb
                                const isBombCategory = category.id === 'bombs';
                                if (isBombCategory) {
                                  // Try to get manufacturer from loaded bomb first
                                  if (hp.loadedItems && hp.loadedItems.length > 0) {
                                    const firstBomb = hp.loadedItems.find((item: any) => {
                                      const liName = (item.itemName || '').toLowerCase();
                                      const liType = (item.itemDefinition?.type || '').toLowerCase();
                                      return liName.startsWith('bomb_') || liType === 'bomb';
                                    });
                                    if (firstBomb?.itemDefinition?.manufacturer) {
                                      mfg = firstBomb.itemDefinition.manufacturer;
                                    }
                                  }
                                  // Fallback to rack manufacturer
                                  if (!mfg) mfg = hp.itemDefinition?.manufacturer;
                                }
                                
                                if (!mfg) mfg = hp.itemDefinition?.manufacturer;
                                return getValidManufacturer(mfg);
                              };

                              return (
                                <div key={hp.id || idx} className={styles.componentCard}>
                                  <div className={styles.componentCardTop}>
                                    <div className={`${styles.componentSize} ${styles[`s${effectiveSize}`]}`}>
                                      S{effectiveSize}
                                    </div>
                                    <div className={styles.componentInfo}>
                                      <h4 className={styles.componentName}>
                                        {getDisplayName()}
                                      </h4>
                                      {getManufacturer() && (
                                        <span className={styles.componentMfg}>{getManufacturer()}</span>
                                      )}
                                      {ftlType && (
                                        <span className={`${styles.typeBadge} ${styles[ftlType.toLowerCase()]}`}>
                                          {ftlType}
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Show Jump Drive as a separate nested item for FTL hardpoints */}
                                  {category.id === 'quantum' && hp.loadedItems && hp.loadedItems.length > 0 && (
                                    <div className={styles.loadedItemsList}>
                                      {hp.loadedItems.map((item: any, itemIdx: number) => {
                                        const itemType = (item.itemDefinition?.type || '').toLowerCase();
                                        const itemName = item.itemName || '';
                                        // Only show Jump Drives (they're loaded inside quantum drive hardpoints)
                                        if (itemType === 'jumpdrive' || itemName.toLowerCase().startsWith('jdrv_')) {
                                          const displayName = item.itemDefinition?.localizedName || 
                                                             formatItemName(itemName.replace(/^JDRV_/i, '').replace(/_SCItem$/i, ''));
                                          const itemSize = item.itemDefinition?.size;
                                          return (
                                            <div key={itemIdx} className={styles.loadedItem}>
                                              <span className={styles.loadedItemName}>{displayName}</span>
                                              {itemSize && <span className={styles.loadedItemSize}>S{itemSize}</span>}
                                              <span className={`${styles.typeBadge} ${styles.jump}`}>Jump</span>
                                            </div>
                                          );
                                        }
                                        return null;
                                      })}
                                    </div>
                                  )}

                                  {/* Show loaded items - weapons show the gun attached to mount (only if mount has children), turrets show guns, missiles show missiles, etc */}
                                  {hp.loadedItems && hp.loadedItems.length > 0 && (
                                    // For weapons: show if there are childAttachments (Structure B) OR if defaultItemName is a mount/turret (Structure A)
                                    // For turrets/missiles/bombs/countermeasures/mining: always show
                                    (category.id === 'weapons' && (
                                      hp.loadedItems.some((item: any) => item.childAttachments && item.childAttachments.length > 0) ||
                                      ((hp.defaultItemName || '').toLowerCase().includes('gimbal') || 
                                       (hp.defaultItemName || '').toLowerCase().includes('mount') ||
                                       (hp.defaultItemName || '').toLowerCase().includes('turret') ||
                                       (hp.defaultItemName || '').toLowerCase().includes('varipuck'))
                                    )) ||
                                    category.id === 'turrets' || 
                                    category.id === 'missiles' || 
                                    category.id === 'bombs' ||
                                    category.id === 'countermeasures' || 
                                    category.id === 'mining'
                                  ) && (
                                    <div className={styles.loadedItemsList}>
                                      {(() => {
                                        // For mining/salvage: show heads with module slots, but NOT for ore pods/cargo storage
                                        if (category.id === 'mining') {
                                          return hp.loadedItems.map((item: any, itemIdx: number) => {
                                            let itemName = item.itemDefinition?.localizedName || item.displayName || '';
                                            const itemSize = item.itemDefinition?.size;
                                            const childAttachments = item.childAttachments || [];
                                            const itemNameRaw = (item.itemName || '').toLowerCase();
                                            const itemType = (item.itemDefinition?.type || '').toLowerCase();
                                            
                                            // Skip modules (SalvageModifier/MiningModifier) at top level - they should only appear as children
                                            // These are items like Abrade, Cinch, Surge, etc. that attach to mining/salvage heads
                                            const isModule = itemType === 'salvagemodifier' || itemType === 'miningmodifier' ||
                                                            itemNameRaw.includes('salvage_modifier') || itemNameRaw.includes('mining_modifier') ||
                                                            itemNameRaw.includes('_scraper') || itemNameRaw.includes('_tractor') ||
                                                            itemNameRaw.includes('abrade') || itemNameRaw.includes('cinch') ||
                                                            itemNameRaw.includes('surge') || itemNameRaw.includes('stampede');
                                            if (isModule) {
                                              return null; // Skip - will be shown as child of mining/salvage head
                                            }
                                            
                                            // Generate better display names for salvage/mining items
                                            if (!itemName || itemName === 'Unknown Item') {
                                              // Check item type and raw name to determine what kind of item this is
                                              if (itemType === 'salvagehead' || itemNameRaw.includes('salvage_head')) {
                                                itemName = 'Salvage Head';
                                              } else if (itemType === 'weaponmining' || itemNameRaw.includes('mining_laser')) {
                                                itemName = 'Mining Laser';
                                              } else if (itemNameRaw.includes('salvagefiller') || itemNameRaw.includes('salvage_filler')) {
                                                itemName = 'Salvage Filler Station';
                                              } else if (itemNameRaw.includes('salvageinternalstorage') || itemNameRaw.includes('salvage_internal')) {
                                                itemName = 'Salvage Internal Storage';
                                              } else if (itemNameRaw.includes('salvagedisintegration') || itemNameRaw.includes('salvage_disintegration')) {
                                                itemName = 'Disintegration Field';
                                              } else if (itemNameRaw.includes('salvagefieldsupporter') || itemNameRaw.includes('salvage_field')) {
                                                itemName = 'Field Supporter';
                                              } else if (itemNameRaw.includes('salvagebuff') || itemNameRaw.includes('salvage_buff')) {
                                                itemName = 'Salvage Modifier';
                                              } else if (itemNameRaw.includes('salvage')) {
                                                // Generic salvage item - try to format nicely
                                                itemName = formatItemName(item.itemName || '').replace(/Drak Vulture|Drak|Vulture/gi, '').trim() || 'Salvage Component';
                                              } else if (item.itemName) {
                                                itemName = formatItemName(item.itemName);
                                              } else {
                                                itemName = 'Attachment';
                                              }
                                            }
                                            
                                            // Check if this is an ore pod or cargo/storage (NOT a mining/salvage head)
                                            const itemNameLower = (item.itemName || '').toLowerCase();
                                            const localizedNameLower = (item.itemDefinition?.localizedName || '').toLowerCase();
                                            const isOrePodOrStorage = itemNameLower.includes('ore_pod') || 
                                                                       itemNameLower.includes('orepod') ||
                                                                       itemNameLower.includes('storage') ||
                                                                       itemNameLower.includes('filler') ||
                                                                       itemNameLower.includes('internalstorage') ||
                                                                       localizedNameLower.includes('ore pod') ||
                                                                       localizedNameLower.includes('storage');
                                            
                                            // Only actual mining/salvage HEADS get module slots
                                            const itemTypeLower = (item.itemDefinition?.type || item.itemName || '').toLowerCase();
                                            const isMiningOrSalvageHead = (itemTypeLower.includes('mining') || itemTypeLower.includes('salvage')) && 
                                                                          !isOrePodOrStorage &&
                                                                          (itemNameLower.includes('laser') || itemNameLower.includes('head') || 
                                                                           itemNameLower.includes('_arm') || itemNameLower.includes('tool'));
                                            const expectedSlots = isMiningOrSalvageHead ? 3 : 0;
                                            
                                            // Don't show anything for ore pods/storage - they'll be grouped separately
                                            if (isOrePodOrStorage) {
                                              return null; // Will be handled by card title, no nested display needed
                                            }
                                            
                                            return (
                                              <div key={itemIdx}>
                                                <div className={styles.loadedItem}>
                                                  <span className={styles.loadedItemName}>{itemName}</span>
                                                  {itemSize && <span className={styles.loadedItemSize}>S{itemSize}</span>}
                                                </div>
                                                {/* Show child attachments (modules on mining/salvage head) */}
                                                {(childAttachments.length > 0 || expectedSlots > 0) && (
                                                  <div className={styles.nestedItems}>
                                                    {childAttachments.map((child: any, childIdx: number) => {
                                                      let childName = child.itemDefinition?.localizedName || child.displayName || '';
                                                      const childSize = child.itemDefinition?.size;
                                                      const childType = (child.itemDefinition?.type || '').toLowerCase();
                                                      const childItemName = (child.itemName || '').toLowerCase();
                                                      const grandChildren = child.childAttachments || [];
                                                      const isModuleHost = childType === 'weaponmining' || childType === 'salvagehead' ||
                                                        childItemName.includes('mining_laser') || childItemName.includes('salvage_head');
                                                      const expectedChildSlots = isModuleHost ? 3 : 0;
                                                      
                                                      // Fix "Unknown Item" or empty names for child attachments
                                                      if (!childName || childName === 'Unknown Item') {
                                                        if (childType === 'salvagehead' || childItemName.includes('salvage_head')) {
                                                          childName = 'Salvage Head';
                                                        } else if (childType === 'salvagemodifier') {
                                                          // Try to get a nice name from the itemName
                                                          if (childItemName.includes('scraper')) {
                                                            childName = 'Scraper Module';
                                                          } else if (childItemName.includes('tractor')) {
                                                            childName = 'Tractor Module';
                                                          } else {
                                                            childName = 'Salvage Module';
                                                          }
                                                        } else if (childType === 'miningmodifier') {
                                                          if (childItemName.includes('surge')) {
                                                            childName = 'Surge Module';
                                                          } else if (childItemName.includes('stampede')) {
                                                            childName = 'Stampede Module';
                                                          } else if (childItemName.includes('vaux')) {
                                                            childName = 'Vaux Module';
                                                          } else {
                                                            childName = 'Mining Module';
                                                          }
                                                        } else if (childItemName) {
                                                          childName = formatItemName(childItemName);
                                                        } else {
                                                          childName = 'Module';
                                                        }
                                                      }
                                                      
                                                      return (
                                                        <div key={childIdx}>
                                                          <div className={styles.loadedItem}>
                                                            <span className={styles.loadedItemName}>└ {childName}</span>
                                                            {childSize && <span className={styles.loadedItemSize}>S{childSize}</span>}
                                                          </div>
                                                          {(grandChildren.length > 0 || expectedChildSlots > 0) && (
                                                            <div className={styles.nestedItems}>
                                                              {grandChildren.map((gc: any, gcIdx: number) => {
                                                                let gcName = gc.itemDefinition?.localizedName || gc.displayName || '';
                                                                const gcSize = gc.itemDefinition?.size;
                                                                const gcType = (gc.itemDefinition?.type || '').toLowerCase();
                                                                const gcItemName = (gc.itemName || '').toLowerCase();
                                                                if (!gcName || gcName === 'Unknown Item') {
                                                                  if (gcType === 'miningmodifier') {
                                                                    if (gcItemName.includes('surge')) gcName = 'Surge Module';
                                                                    else if (gcItemName.includes('stampede')) gcName = 'Stampede Module';
                                                                    else if (gcItemName.includes('vaux')) gcName = 'Vaux Module';
                                                                    else gcName = 'Mining Module';
                                                                  } else if (gcType === 'salvagemodifier') {
                                                                    if (gcItemName.includes('scraper')) gcName = 'Scraper Module';
                                                                    else if (gcItemName.includes('tractor')) gcName = 'Tractor Module';
                                                                    else gcName = 'Salvage Module';
                                                                  } else if (gcItemName) {
                                                                    gcName = formatItemName(gcItemName);
                                                                  } else {
                                                                    gcName = 'Module';
                                                                  }
                                                                }
                                                                return (
                                                                  <div key={gcIdx} className={styles.loadedItem}>
                                                                    <span className={styles.loadedItemName}>└└ {gcName}</span>
                                                                    {gcSize && <span className={styles.loadedItemSize}>S{gcSize}</span>}
                                                                  </div>
                                                                );
                                                              })}
                                                              {expectedChildSlots > grandChildren.length && (
                                                                Array.from({ length: expectedChildSlots - grandChildren.length }).map((_, emptyIdx) => (
                                                                  <div key={`gc-empty-${emptyIdx}`} className={`${styles.loadedItem} ${styles.emptySlot}`}>
                                                                    <span className={styles.loadedItemName}>└└ Empty Module Slot</span>
                                                                    <span className={styles.loadedItemSize}>S1</span>
                                                                  </div>
                                                                ))
                                                              )}
                                                            </div>
                                                          )}
                                                        </div>
                                                      );
                                                    })}
                                                    {/* Show empty slots if this is a mining/salvage head with fewer modules than expected */}
                                                    {expectedSlots > childAttachments.length && (
                                                      Array.from({ length: expectedSlots - childAttachments.length }).map((_, emptyIdx) => (
                                                        <div key={`empty-${emptyIdx}`} className={`${styles.loadedItem} ${styles.emptySlot}`}>
                                                          <span className={styles.loadedItemName}>└ Empty Module Slot</span>
                                                          <span className={styles.loadedItemSize}>S1</span>
                                                        </div>
                                                      ))
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          });
                                        }
                                        
                                        // For countermeasures: show loaded ammo with count
                                        if (category.id === 'countermeasures') {
                                          const ammoCounts: Record<string, { name: string; size?: number; count: number }> = {};
                                          hp.loadedItems.forEach((item: any) => {
                                            const name = item.itemDefinition?.localizedName || item.displayName || formatItemName(item.itemName || '');
                                            const size = item.itemDefinition?.size;
                                            const ammoCount = item.itemDefinition?.countermeasureStats?.ammoCount || 1;
                                            const key = `${name}-${size}`;
                                            if (!ammoCounts[key]) {
                                              ammoCounts[key] = { name, size, count: 0 };
                                            }
                                            ammoCounts[key].count += ammoCount;
                                          });
                                          
                                          return Object.values(ammoCounts).map((ammo, idx) => (
                                            <div key={idx} className={styles.loadedItem}>
                                              <span className={styles.loadedItemName}>
                                                {ammo.count}× {ammo.name}
                                              </span>
                                              {ammo.size && (
                                                <span className={styles.loadedItemSize}>S{ammo.size}</span>
                                              )}
                                            </div>
                                          ));
                                        }
                                        
                                        // For missiles: separate racks/mounts from actual missiles, then nest
                                        if (category.id === 'missiles') {
                                          const rackCounts: Record<string, { name: string; size?: number; count: number }> = {};
                                          const missileCounts: Record<string, { name: string; size?: number; count: number }> = {};
                                          
                                          hp.loadedItems.forEach((item: any) => {
                                            const itemName = (item.itemName || '').toLowerCase();
                                            const itemType = (item.itemDefinition?.type || '').toLowerCase();
                                            const displayName = item.itemDefinition?.localizedName || item.displayName || formatItemName(item.itemName || '');
                                            const size = item.itemDefinition?.size;
                                            const key = `${displayName}-${size}`;
                                            
                                            // Check if it's a rack/mount/turret (container for missiles)
                                            const isRack = itemName.includes('rack') || itemName.includes('turret') || 
                                                          itemType.includes('missilerack') || itemType.includes('turret') ||
                                                          itemType.includes('missileturret');
                                            
                                            if (isRack) {
                                              if (!rackCounts[key]) {
                                                rackCounts[key] = { name: displayName, size, count: 0 };
                                              }
                                              rackCounts[key].count++;
                                            } else {
                                              // It's an actual missile/torpedo
                                              if (!missileCounts[key]) {
                                                missileCounts[key] = { name: displayName, size, count: 0 };
                                              }
                                              missileCounts[key].count++;
                                            }
                                          });
                                          
                                          const rackList = Object.values(rackCounts);
                                          const missileList = Object.values(missileCounts);
                                          
                                          // If we have racks, show rack > missiles hierarchy
                                          if (rackList.length > 0) {
                                            return (
                                              <>
                                                {rackList.map((rack, idx) => (
                                                  <div key={`rack-${idx}`} className={styles.loadedItem}>
                                                    <span className={styles.loadedItemName}>
                                                      {rack.count > 1 ? `${rack.count}× ` : ''}{rack.name}
                                                    </span>
                                                    {rack.size && <span className={styles.loadedItemSize}>S{rack.size}</span>}
                                                  </div>
                                                ))}
                                                {/* Missiles nested under racks */}
                                                {missileList.length > 0 && (
                                                  <div className={styles.nestedItems}>
                                                    {missileList.map((missile, idx) => (
                                                      <div key={`missile-${idx}`} className={styles.loadedItem}>
                                                        <span className={styles.loadedItemName}>
                                                          └ {missile.count > 1 ? `${missile.count}× ` : ''}{missile.name}
                                                        </span>
                                                        {missile.size && <span className={styles.loadedItemSize}>S{missile.size}</span>}
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                              </>
                                            );
                                          } else {
                                            // No racks, just missiles directly
                                            return missileList.map((missile, idx) => (
                                              <div key={idx} className={styles.loadedItem}>
                                                <span className={styles.loadedItemName}>
                                                  {missile.count > 1 ? `${missile.count}× ` : ''}{missile.name}
                                                </span>
                                                {missile.size && <span className={styles.loadedItemSize}>S{missile.size}</span>}
                                              </div>
                                            ));
                                          }
                                        }
                                        
                                        // For bombs: separate bomb racks/consoles from actual bombs, then nest
                                        if (category.id === 'bombs') {
                                          const rackCounts: Record<string, { name: string; size?: number; count: number }> = {};
                                          const bombCounts: Record<string, { name: string; size?: number; count: number }> = {};
                                          
                                          hp.loadedItems.forEach((item: any) => {
                                            const itemName = (item.itemName || '').toLowerCase();
                                            const itemType = (item.itemDefinition?.type || '').toLowerCase();
                                            const displayName = item.itemDefinition?.localizedName || item.displayName || formatItemName(item.itemName || '');
                                            const size = item.itemDefinition?.size;
                                            const key = `${displayName}-${size}`;
                                            
                                            // Check if it's a rack/console (container for bombs)
                                            const isRack = itemName.includes('rack') || itemName.includes('console') || 
                                                          itemName.includes('bombrack') || itemType.includes('bombrack') ||
                                                          itemType === 'turretbase';
                                            
                                            // Check if it's an actual bomb
                                            const isBombItem = itemName.startsWith('bomb_') || itemType === 'bomb';
                                            
                                            if (isRack) {
                                              if (!rackCounts[key]) {
                                                rackCounts[key] = { name: displayName, size, count: 0 };
                                              }
                                              rackCounts[key].count++;
                                            } else if (isBombItem) {
                                              if (!bombCounts[key]) {
                                                bombCounts[key] = { name: displayName, size, count: 0 };
                                              }
                                              bombCounts[key].count++;
                                            }
                                          });
                                          
                                          const rackList = Object.values(rackCounts);
                                          const bombList = Object.values(bombCounts);
                                          
                                          // If we have racks, show rack > bombs hierarchy
                                          if (rackList.length > 0) {
                                            return (
                                              <>
                                                {rackList.map((rack, idx) => (
                                                  <div key={`rack-${idx}`} className={styles.loadedItem}>
                                                    <span className={styles.loadedItemName}>
                                                      {rack.count > 1 ? `${rack.count}× ` : ''}{rack.name}
                                                    </span>
                                                    {rack.size && <span className={styles.loadedItemSize}>S{rack.size}</span>}
                                                  </div>
                                                ))}
                                                {/* Bombs nested under racks */}
                                                {bombList.length > 0 && (
                                                  <div className={styles.nestedItems}>
                                                    {bombList.map((bomb, idx) => (
                                                      <div key={`bomb-${idx}`} className={styles.loadedItem}>
                                                        <span className={styles.loadedItemName}>
                                                          └ {bomb.count > 1 ? `${bomb.count}× ` : ''}{bomb.name}
                                                        </span>
                                                        {bomb.size && <span className={styles.loadedItemSize}>S{bomb.size}</span>}
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                              </>
                                            );
                                          } else {
                                            // No racks, just bombs directly
                                            return bombList.map((bomb, idx) => (
                                              <div key={idx} className={styles.loadedItem}>
                                                <span className={styles.loadedItemName}>
                                                  {bomb.count > 1 ? `${bomb.count}× ` : ''}{bomb.name}
                                                </span>
                                                {bomb.size && <span className={styles.loadedItemSize}>S{bomb.size}</span>}
                                              </div>
                                            ));
                                          }
                                        }
                                        
                                        // For TURRETS: Items can be mounts OR guns, all at same level in loadedItems
                                        // Need to separate them by detecting item type/name
                                        if (category.id === 'turrets') {
                                          // Debug: Log full structure to understand data
                                          console.log('=== TURRET FULL DEBUG ===');
                                          console.log('Turret name:', hp.itemDefinition?.localizedName || hp.defaultItemName);
                                          console.log('Turret slotName:', hp.slotName);
                                          console.log('Turret hardpointType:', hp.hardpointType);
                                          console.log('Turret itemDef type:', hp.itemDefinition?.type);
                                          hp.loadedItems?.forEach((item: any, idx: number) => {
                                            const weaponStats = item.itemDefinition?.weaponStats;
                                            console.log(`  LoadedItem[${idx}]:`, {
                                              itemName: item.itemName,
                                              displayName: item.displayName,
                                              localizedName: item.itemDefinition?.localizedName,
                                              type: item.itemDefinition?.type,
                                              size: item.itemDefinition?.size,
                                              parentId: item.parentAttachmentId,
                                              childAttachments: item.childAttachments?.length || 0,
                                              // Weapon stats for reverse-engineering gun count
                                              weaponStats: weaponStats ? {
                                                damagePerShot: weaponStats.damagePerShot,
                                                fireRate: weaponStats.fireRate,
                                                dps: weaponStats.damagePerShot * (weaponStats.fireRate || 60) / 60
                                              } : null
                                            });
                                          });
                                          console.log('=== END TURRET ===');
                                          
                                          // Separate mounts from guns - but EXCLUDE turret types from showing as nested items
                                          const mountCounts: Record<string, { name: string; size?: number; count: number }> = {};
                                          const gunCounts: Record<string, { name: string; size?: number; count: number }> = {};
                                          
                                          hp.loadedItems.forEach((item: any) => {
                                            const itemName = (item.itemName || '').toLowerCase();
                                            const itemType = (item.itemDefinition?.type || '').toLowerCase();
                                            const displayName = item.itemDefinition?.localizedName || item.displayName || formatItemName(item.itemName || '');
                                            const size = item.itemDefinition?.size;
                                            const key = `${displayName}-${size}`;
                                            
                                            // SKIP TurretBase types - they're the turret itself, not a component
                                            // But keep "Turret" type - those are actually mounts/gimbals!
                                            if (itemType === 'turretbase') {
                                              console.log(`  SKIPPING turretbase item: ${itemName}`);
                                              return;
                                            }
                                            
                                            // Check if it's a mount/gimbal 
                                            // Type "Turret" in loadedItems means it's actually a mount/gimbal
                                            const isMount = itemName.includes('mount') || itemName.includes('gimbal') || 
                                                           itemType === 'turret';
                                            
                                            // Check if it's a weapon/gun
                                            const isWeaponType = itemType === 'weapon' || itemType === 'weapongun';
                                            
                                            console.log(`  ${itemName} -> isMount: ${isMount}, isWeapon: ${isWeaponType}, type: ${itemType}`);
                                            
                                            if (isMount && !isWeaponType) {
                                              if (!mountCounts[key]) {
                                                mountCounts[key] = { name: displayName, size, count: 0 };
                                              }
                                              mountCounts[key].count++;
                                            } else if (isWeaponType) {
                                              // It's a gun/weapon
                                              if (!gunCounts[key]) {
                                                gunCounts[key] = { name: displayName, size, count: 0 };
                                              }
                                              gunCounts[key].count++;
                                            }
                                          });
                                          
                                          const mountList = Object.values(mountCounts);
                                          const gunList = Object.values(gunCounts);
                                          
                                          // If we have mounts, show mount > guns hierarchy
                                          if (mountList.length > 0) {
                                            return (
                                              <>
                                                {mountList.map((mount, idx) => (
                                                  <div key={`mount-${idx}`} className={styles.loadedItem}>
                                                    <span className={styles.loadedItemName}>
                                                      {mount.count > 1 ? `${mount.count}× ` : ''}{mount.name}
                                                    </span>
                                                    {mount.size && <span className={styles.loadedItemSize}>S{mount.size}</span>}
                                                  </div>
                                                ))}
                                                {/* Guns nested under mounts */}
                                                {gunList.length > 0 && (
                                                  <div className={styles.nestedItems}>
                                                    {gunList.map((gun, idx) => (
                                                      <div key={`gun-${idx}`} className={styles.loadedItem}>
                                                        <span className={styles.loadedItemName}>
                                                          └ {gun.count > 1 ? `${gun.count}× ` : ''}{gun.name}
                                                        </span>
                                                        {gun.size && <span className={styles.loadedItemSize}>S{gun.size}</span>}
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                              </>
                                            );
                                          } else {
                                            // No mounts, just guns directly on turret
                                            return gunList.map((gun, idx) => (
                                              <div key={idx} className={styles.loadedItem}>
                                                <span className={styles.loadedItemName}>
                                                  {gun.count > 1 ? `${gun.count}× ` : ''}{gun.name}
                                                </span>
                                                {gun.size && <span className={styles.loadedItemSize}>S{gun.size}</span>}
                                              </div>
                                            ));
                                          }
                                        }
                                        
                                        // For WEAPONS: show gun(s) nested under the mount
                                        // Structure A: hp.defaultItemName=Mount, hp.loadedItems[0]=Gun (Aurora, Gladius)
                                        // Structure B: hp.loadedItems[0]=Mount with childAttachments[0]=Gun (Polaris)
                                        if (category.id === 'weapons') {
                                          const gunCounts: Record<string, { name: string; size?: number; count: number }> = {};
                                          
                                          // Check which structure we have
                                          const defaultName = (hp.defaultItemName || '').toLowerCase();
                                          const isMountInDefault = defaultName.includes('gimbal') || defaultName.includes('mount') || 
                                                                   defaultName.includes('turret') || defaultName.includes('varipuck');
                                          
                                          hp.loadedItems.forEach((item: any) => {
                                            const itemName = (item.itemName || '').toLowerCase();
                                            const displayName = item.itemDefinition?.localizedName || item.displayName || formatItemName(item.itemName || '');
                                            const size = item.itemDefinition?.size || item.size;
                                            
                                            // Structure B: loadedItem has childAttachments (guns nested under mount in loadedItems)
                                            if (item.childAttachments && item.childAttachments.length > 0) {
                                              item.childAttachments.forEach((child: any) => {
                                                const gunName = child.itemDefinition?.localizedName || child.displayName || formatItemName(child.itemName || '');
                                                const gunSize = child.itemDefinition?.size || child.size;
                                                const gunKey = `${gunName}-${gunSize}`;
                                                if (!gunCounts[gunKey]) {
                                                  gunCounts[gunKey] = { name: gunName, size: gunSize, count: 0 };
                                                }
                                                gunCounts[gunKey].count++;
                                              });
                                            } else if (isMountInDefault) {
                                              // Structure A: loadedItem IS the gun (mount is in defaultItemName)
                                              // Only show if it's actually a weapon, not a mount/gimbal
                                              const itemType = (item.itemDefinition?.type || '').toLowerCase();
                                              const isItemMount = itemName.includes('gimbal') || itemName.includes('mount') || 
                                                                 itemType.includes('turret') || itemType.includes('gimbal');
                                              
                                              if (!isItemMount && displayName && !displayName.includes('PLACEHOLDER')) {
                                                const gunKey = `${displayName}-${size}`;
                                                if (!gunCounts[gunKey]) {
                                                  gunCounts[gunKey] = { name: displayName, size, count: 0 };
                                                }
                                                gunCounts[gunKey].count++;
                                              }
                                            }
                                            // If neither structure matches, don't show nested items
                                          });
                                          
                                          const gunList = Object.values(gunCounts);
                                          
                                          // Show guns nested under the mount (card title shows the mount)
                                          if (gunList.length > 0) {
                                            return (
                                              <div className={styles.nestedItems}>
                                                {gunList.map((gun, idx) => (
                                                  <div key={`gun-${idx}`} className={styles.loadedItem}>
                                                    <span className={styles.loadedItemName}>
                                                      └ {gun.count > 1 ? `${gun.count}× ` : ''}{gun.name}
                                                    </span>
                                                    {gun.size && <span className={styles.loadedItemSize}>S{gun.size}</span>}
                                                  </div>
                                                ))}
                                              </div>
                                            );
                                          }
                                          return null;
                                        }
                                        
                                        // Default: show loaded items directly (for other categories)
                                        const itemCounts: Record<string, { name: string; size?: number; count: number }> = {};
                                        hp.loadedItems.forEach((item: any) => {
                                          const displayName = item.itemDefinition?.localizedName || item.displayName || formatItemName(item.itemName || '');
                                          const size = item.itemDefinition?.size;
                                          const key = `${displayName}-${size}`;
                                          if (!itemCounts[key]) {
                                            itemCounts[key] = { name: displayName, size, count: 0 };
                                          }
                                          itemCounts[key].count++;
                                        });
                                        
                                        return Object.values(itemCounts).map((item, idx) => (
                                          <div key={idx} className={styles.loadedItem}>
                                            <span className={styles.loadedItemName}>
                                              {item.count > 1 ? `${item.count}× ` : ''}{item.name}
                                            </span>
                                            {item.size && (
                                              <span className={styles.loadedItemSize}>S{item.size}</span>
                                            )}
                                          </div>
                                        ));
                                      })()}
                                    </div>
                                  )}

                                  {/* Weapon stats - for weapons category, get stats from actual gun in childAttachments */}
                                  {(() => {
                                    // Find weapon stats - could be on hp.itemDefinition (direct weapon) or in loadedItems/childAttachments (mounted weapon)
                                    let weaponStats = hp.itemDefinition?.weaponStats;
                                    
                                    // For weapons category with mounts, dig into childAttachments to find the actual gun stats
                                    if (!weaponStats && isWeapon && hp.loadedItems && hp.loadedItems.length > 0) {
                                      const firstItem = hp.loadedItems[0];
                                      // Check if mount has childAttachments (the actual guns)
                                      if (firstItem.childAttachments && firstItem.childAttachments.length > 0) {
                                        weaponStats = firstItem.childAttachments[0].itemDefinition?.weaponStats;
                                      } else {
                                        // No childAttachments - check if the loadedItem itself is a weapon
                                        weaponStats = firstItem.itemDefinition?.weaponStats;
                                      }
                                    }
                                    
                                    if (!weaponStats) return null;
                                    
                                    return (
                                      <div className={styles.componentCardStats}>
                                        {weaponStats.damagePerShot && (
                                          <div className={styles.componentStat}>
                                            <Target size={14} />
                                            <span className={styles.value}>{formatCompact(weaponStats.damagePerShot)}</span>
                                            <span>dmg</span>
                                          </div>
                                        )}
                                        {weaponStats.fireRate && (
                                          <div className={styles.componentStat}>
                                            <Clock size={14} />
                                            <span className={styles.value}>{weaponStats.fireRate}</span>
                                            <span>rpm</span>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}

                                  {/* Shield stats */}
                                  {hp.itemDefinition?.shieldStats && (
                                    <div className={styles.componentCardStats}>
                                      {hp.itemDefinition.shieldStats.shieldHealthMax && (
                                        <div className={styles.componentStat}>
                                          <ShieldCheck size={14} />
                                          <span className={styles.value}>{formatCompact(hp.itemDefinition.shieldStats.shieldHealthMax)}</span>
                                          <span>HP</span>
                                        </div>
                                      )}
                                      {hp.itemDefinition.shieldStats.regenRate && (
                                        <div className={styles.componentStat}>
                                          <RefreshCw size={14} />
                                          <span className={styles.value}>{formatCompact(hp.itemDefinition.shieldStats.regenRate)}</span>
                                          <span>/s</span>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* Power plant stats */}
                                  {hp.itemDefinition?.powerPlantStats && (
                                    <div className={styles.componentCardStats}>
                                      {hp.itemDefinition.powerPlantStats.powerOutput && (
                                        <div className={styles.componentStat}>
                                          <Zap size={14} />
                                          <span className={styles.value}>{formatCompact(hp.itemDefinition.powerPlantStats.powerOutput)}</span>
                                          <span>pips</span>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* Cooler stats */}
                                  {hp.itemDefinition?.coolerStats && (
                                    <div className={styles.componentCardStats}>
                                      {hp.itemDefinition.coolerStats.coolingRate && (
                                        <div className={styles.componentStat}>
                                          <Thermometer size={14} />
                                          <span className={styles.value}>{formatCompact(hp.itemDefinition.coolerStats.coolingRate)}</span>
                                          <span>/s</span>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* QD stats */}
                                  {hp.itemDefinition?.quantumDriveStats && (
                                    <div className={styles.componentCardStats}>
                                      {hp.itemDefinition.quantumDriveStats.driveSpeed && (
                                        <div className={styles.componentStat}>
                                          <Sparkles size={14} />
                                          <span className={styles.value}>{formatCompact(hp.itemDefinition.quantumDriveStats.driveSpeed)}</span>
                                          <span>m/s</span>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* Missile stats */}
                                  {hp.itemDefinition?.missileStats && (
                                    <div className={styles.componentCardStats}>
                                      {hp.itemDefinition.missileStats.damage && (
                                        <div className={styles.componentStat}>
                                          <Rocket size={14} />
                                          <span className={styles.value}>{formatCompact(hp.itemDefinition.missileStats.damage)}</span>
                                          <span>dmg</span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Reviews Tab */}
          {activeTab === 'facilities' && (
            <div className={styles.facilitiesSection}>
              <p className={styles.facilitiesNote}>
                What this ship actually has on board. Amber items are details
                we do not track yet — that is different from a real no.
              </p>
              {facilitiesFailed && (
                <p className={styles.facilitiesNote}>Could not load facilities.</p>
              )}
              {!facilitiesFailed && facilities === null && (
                <p className={styles.facilitiesNote}>Loading…</p>
              )}
              {facilities?.map((f) => (
                <div className={styles.facilityRow} key={f.key}>
                  <div className={styles.facilityInfo}>
                    <span className={styles.facilityLabel}>{f.label}</span>
                    {f.detail && (
                      <span className={styles.facilityDetail}>{f.detail}</span>
                    )}
                  </div>
                  <span
                    className={`${styles.facilityBadge} ${
                      f.status === 'yes'
                        ? styles.facilityYes
                        : f.status === 'pending'
                        ? styles.facilityPending
                        : styles.facilityNo
                    }`}
                  >
                    {f.status === 'yes'
                      ? f.count > 1
                        ? `x${f.count}`
                        : 'Yes'
                      : f.status === 'pending'
                      ? 'TBD'
                      : 'None'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className={styles.reviewsSection}>
              {/* Reviews Header with Average Rating */}
              <div className={styles.reviewsHeader}>
                <div className={styles.reviewsSummary}>
                  <div className={styles.avgRatingDisplay}>
                    <Star size={32} className={styles.avgRatingStar} fill="#fbbf24" />
                    <span className={styles.avgRatingValue}>
                      {reviewSummary ? reviewSummary.averageRating.toFixed(1) : '—'}
                    </span>
                    <span className={styles.avgRatingMax}>/5</span>
                  </div>
                  <div className={styles.reviewsCount}>
                    {reviewSummary?.totalReviews || 0} review{reviewSummary?.totalReviews !== 1 ? 's' : ''}
                  </div>
                  
                  {/* Rating Distribution */}
                  {reviewSummary && reviewSummary.totalReviews > 0 && (
                    <div className={styles.ratingDistribution}>
                      {[5, 4, 3, 2, 1].map(star => {
                        const count = reviewSummary.distribution[star] || 0;
                        const percentage = reviewSummary.totalReviews > 0 
                          ? (count / reviewSummary.totalReviews) * 100 
                          : 0;
                        return (
                          <div key={star} className={styles.ratingBar}>
                            <span className={styles.ratingBarLabel}>{star}</span>
                            <Star size={12} fill="#fbbf24" />
                            <div className={styles.ratingBarTrack}>
                              <div 
                                className={styles.ratingBarFill} 
                                style={{ width: `${percentage}%` }} 
                              />
                            </div>
                            <span className={styles.ratingBarCount}>{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Write Review Button */}
                <div className={styles.reviewActions}>
                  {isAuthenticated ? (
                    userReviewId ? (
                      <button 
                        className={styles.editReviewBtn}
                        onClick={() => {
                          const myReview = reviews.find(r => r.id === userReviewId);
                          if (myReview) startEditReview(myReview);
                        }}
                      >
                        <Edit2 size={16} />
                        Edit Your Review
                      </button>
                    ) : (
                      <button 
                        className={styles.writeReviewBtn}
                        onClick={() => setShowReviewForm(true)}
                      >
                        <MessageSquare size={16} />
                        Write a Review
                      </button>
                    )
                  ) : (
                    <p className={styles.loginPrompt}>
                      <Lock size={14} />
                      Log in to write a review
                    </p>
                  )}
                </div>
              </div>

              {/* Review Form */}
              {showReviewForm && (
                <div className={styles.reviewForm}>
                  <h3>{editingReview ? 'Edit Your Review' : 'Write a Review'}</h3>
                  
                  {/* Star Rating Input */}
                  <div className={styles.ratingInput}>
                    <span className={styles.ratingInputLabel}>Your Rating:</span>
                    <div className={styles.starInput}>
                      {[1, 2, 3, 4, 5].map(star => (
                        <button
                          key={star}
                          type="button"
                          className={`${styles.starBtn} ${star <= reviewRating ? styles.filled : ''}`}
                          onClick={() => setReviewRating(star)}
                        >
                          <Star size={24} fill={star <= reviewRating ? '#fbbf24' : 'none'} />
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Review Text */}
                  <div className={styles.reviewTextInput}>
                    <label>Review (optional):</label>
                    <textarea
                      value={reviewText}
                      onChange={(e) => setReviewText(e.target.value)}
                      placeholder="Share your experience with this ship..."
                      maxLength={2000}
                      rows={4}
                    />
                    <span className={styles.charCount}>{reviewText.length}/2000</span>
                  </div>

                  {reviewError && (
                    <div className={styles.reviewError}>
                      <AlertTriangle size={16} />
                      {reviewError}
                    </div>
                  )}

                  <div className={styles.reviewFormActions}>
                    <button 
                      className={styles.cancelBtn}
                      onClick={cancelReviewForm}
                      disabled={reviewSubmitting}
                    >
                      Cancel
                    </button>
                    <button 
                      className={styles.submitBtn}
                      onClick={handleSubmitReview}
                      disabled={reviewSubmitting}
                    >
                      {reviewSubmitting ? 'Submitting...' : (editingReview ? 'Update Review' : 'Submit Review')}
                    </button>
                  </div>
                </div>
              )}

              {/* Sort Options */}
              <div className={styles.reviewsControls}>
                <label>Sort by:</label>
                <select 
                  value={reviewsSort} 
                  onChange={(e) => {
                    setReviewsSort(e.target.value as SortOption);
                    setReviewsPage(1);
                  }}
                >
                  <option value="helpful">Most Helpful</option>
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="highest">Highest Rated</option>
                  <option value="lowest">Lowest Rated</option>
                </select>
              </div>

              {/* Reviews List */}
              {reviewsLoading ? (
                <div className={styles.reviewsLoading}>Loading reviews...</div>
              ) : reviews.length === 0 ? (
                <div className={styles.noReviews}>
                  <MessageSquare size={48} />
                  <h3>No reviews yet</h3>
                  <p>Be the first to review this ship!</p>
                </div>
              ) : (
                <div className={styles.reviewsList}>
                  {reviews.map(review => (
                    <div key={review.id} className={`${styles.reviewCard} ${review.id === userReviewId ? styles.myReview : ''}`}>
                      <div className={styles.reviewCardHeader}>
                        <div className={styles.reviewerInfo}>
                          <span className={styles.reviewerName}>{review.username}</span>
                          <div className={styles.reviewRating}>
                            {[1, 2, 3, 4, 5].map(star => (
                              <Star 
                                key={star} 
                                size={14} 
                                fill={star <= review.rating ? '#fbbf24' : 'none'}
                                className={star <= review.rating ? styles.filledStar : styles.emptyStar}
                              />
                            ))}
                          </div>
                        </div>
                        <span className={styles.reviewDate}>
                          {new Date(review.createdAt).toLocaleDateString()}
                          {review.updatedAt !== review.createdAt && ' (edited)'}
                        </span>
                      </div>

                      {review.reviewText && (
                        <p className={styles.reviewText}>{review.reviewText}</p>
                      )}

                      <div className={styles.reviewCardFooter}>
                        <div className={styles.voteButtons}>
                          <button
                            className={`${styles.voteBtn} ${styles.upvote} ${myVotes[review.id] === true ? styles.voted : ''}`}
                            onClick={() => handleVote(review.id, true)}
                            disabled={!isAuthenticated || review.userId === user?.id}
                            title={!isAuthenticated ? 'Log in to vote' : review.userId === user?.id ? "Can't vote on your own review" : 'Helpful'}
                          >
                            <ThumbsUp size={14} />
                            <span>{review.upvoteCount}</span>
                          </button>
                          <button
                            className={`${styles.voteBtn} ${styles.downvote} ${myVotes[review.id] === false ? styles.voted : ''}`}
                            onClick={() => handleVote(review.id, false)}
                            disabled={!isAuthenticated || review.userId === user?.id}
                            title={!isAuthenticated ? 'Log in to vote' : review.userId === user?.id ? "Can't vote on your own review" : 'Not helpful'}
                          >
                            <ThumbsDown size={14} />
                            <span>{review.downvoteCount}</span>
                          </button>
                        </div>

                        {/* Edit/Delete for own reviews */}
                        {review.id === userReviewId && (
                          <div className={styles.reviewOwnerActions}>
                            <button 
                              className={styles.editBtn}
                              onClick={() => startEditReview(review)}
                            >
                              <Edit2 size={14} />
                              Edit
                            </button>
                            <button 
                              className={styles.deleteBtn}
                              onClick={() => handleDeleteReview(review.id)}
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {reviewsTotalPages > 1 && (
                <div className={styles.reviewsPagination}>
                  <button 
                    disabled={reviewsPage <= 1}
                    onClick={() => setReviewsPage(p => p - 1)}
                  >
                    Previous
                  </button>
                  <span>Page {reviewsPage} of {reviewsTotalPages}</span>
                  <button 
                    disabled={reviewsPage >= reviewsTotalPages}
                    onClick={() => setReviewsPage(p => p + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Disclaimer */}
      <div className={styles.disclaimer}>
        <Info size={16} />
        <p>
          This is an unofficial fan-made project and is not affiliated with or endorsed by Cloud Imperium Games 
          or Roberts Space Industries. Star Citizen® and all associated marks are the property of their respective owners.
        </p>
      </div>

      {/* Support Banner */}
      <div className={styles.supportBanner}>
        <p>Enjoy this free resource? Consider supporting development!</p>
        <a 
          href="https://ko-fi.com/digitalhorizonlabs" 
          target="_blank" 
          rel="noopener noreferrer"
          className={styles.kofiButton}
        >
          ☕ Support on Ko-fi
        </a>
      </div>
    </div>
  );
};

export default ShipDetailV2;
